/**
 * Enumerations shared by the database, the API, and every client.
 *
 * Declared as `as const` arrays rather than TypeScript `enum`s: the array is directly usable as a
 * Zod `z.enum()`, as a Prisma enum's source of truth, and as a runtime list for populating a
 * dropdown. A TS `enum` gives none of those without a conversion step.
 */

// ---------------------------------------------------------------------------
// Geography & legal
// ---------------------------------------------------------------------------

/** ISO 3166-1 alpha-2 for the countries with a payroll pack, planned or shipped. */
export const COUNTRIES = ['IN', 'US', 'GB', 'AE', 'SG', 'CA', 'AU', 'DE', 'NL'] as const;
export type Country = (typeof COUNTRIES)[number];

/** Display names for `COUNTRIES` — a fixed map rather than `Intl.DisplayNames`, which React Native's
 *  Hermes engine does not fully support and which can vary by runtime. */
export const COUNTRY_NAMES: Record<Country, string> = {
  IN: 'India',
  US: 'United States',
  GB: 'United Kingdom',
  AE: 'United Arab Emirates',
  SG: 'Singapore',
  CA: 'Canada',
  AU: 'Australia',
  DE: 'Germany',
  NL: 'Netherlands',
};

/** ISO 4217. */
export const CURRENCIES = ['INR', 'USD', 'GBP', 'AED', 'SGD', 'CAD', 'AUD', 'EUR'] as const;
export type Currency = (typeof CURRENCIES)[number];

/**
 * Indian states and union territories — Professional Tax and Labour Welfare Fund are levied
 * state-wise, with different slabs, frequencies, and remittance calendars in each.
 */
export const IN_STATES = [
  'AN',
  'AP',
  'AR',
  'AS',
  'BR',
  'CH',
  'CT',
  'DH',
  'DL',
  'GA',
  'GJ',
  'HR',
  'HP',
  'JK',
  'JH',
  'KA',
  'KL',
  'LA',
  'LD',
  'MP',
  'MH',
  'MN',
  'ML',
  'MZ',
  'NL',
  'OR',
  'PY',
  'PB',
  'RJ',
  'SK',
  'TN',
  'TS',
  'TR',
  'UP',
  'UT',
  'WB',
] as const;
export type InState = (typeof IN_STATES)[number];

/** Languages the employee-facing surfaces are translated into. */
export const LOCALES = ['en', 'hi', 'ta', 'te', 'kn', 'bn', 'gu', 'mr', 'ml', 'pa'] as const;
export type Locale = (typeof LOCALES)[number];

// ---------------------------------------------------------------------------
// Employment
// ---------------------------------------------------------------------------

export const EMPLOYMENT_TYPES = [
  'full_time',
  'part_time',
  'contract',
  'intern',
  'consultant',
  'gig',
  'apprentice',
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const EMPLOYMENT_STATUSES = [
  'pre_joining',
  'probation',
  'confirmed',
  'on_leave',
  'notice_period',
  'exited',
  'rescinded',
] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];

/**
 * The 7 exit/offboarding reasons every org used to be stuck with. Now org-customizable (see the
 * `ExitType` Prisma model and org-setup.contract.ts) — kept here ONLY as the default set a fresh
 * org is provisioned with (packages/db/src/provisioning/exit-type-defaults.ts). Nothing validates
 * against this array any more; `exitTypeSchema` (schemas/common.ts) is a plain string, checked at
 * the API layer against the calling org's own ExitType rows.
 */
export const EXIT_TYPES = [
  'resignation',
  'termination',
  'retirement',
  'end_of_contract',
  'redundancy',
  'abandonment',
  'death',
] as const;

// ---------------------------------------------------------------------------
// Offboarding
// ---------------------------------------------------------------------------

/** Rendering hint for OffboardingTaskTemplate/OffboardingTask — fixed because each kind maps to
 *  a specific frontend widget. Which tasks of which kind exist is fully org-configurable; the
 *  kind list itself is not (see docs/plans/offboarding/offboarding-module-plan.md §3). */
export const OFFBOARDING_TASK_KINDS = [
  'generic',
  'exit_interview',
  'knowledge_transfer',
  'asset_return',
  'clearance',
  'fnf',
] as const;
export type OffboardingTaskKind = (typeof OFFBOARDING_TASK_KINDS)[number];

export const OFFBOARDING_ASSIGNEE_ROLES = ['HR', 'MANAGER', 'IT', 'FINANCE', 'EMPLOYEE'] as const;
export type OffboardingAssigneeRole = (typeof OFFBOARDING_ASSIGNEE_ROLES)[number];

export const OFFBOARDING_TASK_STATUSES = [
  'pending',
  'in_progress',
  'completed',
  'skipped',
] as const;
export type OffboardingTaskStatus = (typeof OFFBOARDING_TASK_STATUSES)[number];

export const OFFBOARDING_PROCESS_STATUSES = [
  'active',
  'on_hold',
  'archived',
  'cancelled',
] as const;
export type OffboardingProcessStatus = (typeof OFFBOARDING_PROCESS_STATUSES)[number];

export const EXIT_INTERVIEW_QUESTION_TYPES = ['rating', 'choice', 'text'] as const;
export type ExitInterviewQuestionType = (typeof EXIT_INTERVIEW_QUESTION_TYPES)[number];

export const GENDERS = ['male', 'female', 'non_binary', 'other', 'undisclosed'] as const;
export type Gender = (typeof GENDERS)[number];

export const MARITAL_STATUSES = ['single', 'married', 'divorced', 'widowed'] as const;
export type MaritalStatus = (typeof MARITAL_STATUSES)[number];

/** On-site / remote / hybrid — a work arrangement, distinct from EMPLOYMENT_TYPES' contractual
 * category (full-time/part-time/contract/...). */
export const WORK_TYPES = ['on_site', 'remote', 'hybrid'] as const;
export type WorkType = (typeof WORK_TYPES)[number];

// ---------------------------------------------------------------------------
// Workflow
// ---------------------------------------------------------------------------

export const WORKFLOW_STATUSES = [
  'draft',
  'pending',
  'approved',
  'rejected',
  'withdrawn',
  'expired',
  /** Retired in favour of a fresh instance for the same entity (F10) — never the initial status of
   * one, and never produced by `decide`/`withdraw`/`escalate`. Every mapping from `WorkflowStatus`
   * to a domain's own vocabulary folds it through that switch's existing `default`, exactly like an
   * unrecognised value would, because only `payroll_run`'s reopen path produces it today. */
  'superseded',
] as const;
export type WorkflowStatus = (typeof WORKFLOW_STATUSES)[number];

export const APPROVAL_DECISIONS = [
  'approved',
  'rejected',
  'delegated',
  'skipped',
  'info_requested',
] as const;
export type ApprovalDecision = (typeof APPROVAL_DECISIONS)[number];

/**
 * How a workflow step finds its approver. Most are computed at approval time rather than stored,
 * so a reorg does not strand an in-flight request with a departed approver.
 */
export const APPROVER_RULES = [
  'reporting_manager',
  'skip_level',
  'department_head',
  'entity_head',
  'cost_centre_owner',
  'role',
  'specific_person',
] as const;
export type ApproverRule = (typeof APPROVER_RULES)[number];

// ---------------------------------------------------------------------------
// Leave & attendance
// ---------------------------------------------------------------------------

export const LEAVE_UNITS = ['day', 'half_day', 'hour'] as const;
export type LeaveUnit = (typeof LEAVE_UNITS)[number];

/** A leave request's lifecycle. `cancelled` mirrors a withdrawn workflow instance. */
export const LEAVE_REQUEST_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
  'withdrawn',
] as const;
export type LeaveRequestStatus = (typeof LEAVE_REQUEST_STATUSES)[number];

export const ACCRUAL_FREQUENCIES = [
  'none',
  'monthly',
  'quarterly',
  'half_yearly',
  'annually',
  'per_pay_period',
] as const;
export type AccrualFrequency = (typeof ACCRUAL_FREQUENCIES)[number];

export const ATTENDANCE_SOURCES = [
  'mobile_gps',
  'web',
  'kiosk',
  'qr',
  'biometric',
  'manual',
  'imported',
] as const;
export type AttendanceSource = (typeof ATTENDANCE_SOURCES)[number];

export const ATTENDANCE_STATUSES = [
  'present',
  'absent',
  'half_day',
  'weekly_off',
  'holiday',
  'on_leave',
  'lop',
] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];

// ---------------------------------------------------------------------------
// Expenses & reimbursement
// ---------------------------------------------------------------------------

/**
 * An expense claim's lifecycle.
 *
 * Wider than `WORKFLOW_STATUSES` at both ends: a claim exists as a `draft` before any workflow
 * instance does, and continues to `settled` after the instance has already closed as `approved` —
 * approving a claim authorises the money, paying it out is a separate finance action. `rejected`
 * is terminal.
 *
 * `cancelled` (EXP-P1-9) — a terminal state for an `approved` claim that is stopped before any
 * money moves: distinct from `withdrawn` (the claimant pulled back their OWN submission before
 * anyone decided it) because `cancelled` happens AFTER approval, so it needs to be told apart in
 * reporting from a claim that was simply never decided. Only reachable from `approved`, and only
 * while the claim has not yet joined a payment batch or been reserved by a payroll run — once
 * either of those has happened, undoing it is a different problem (removing it from the batch/run
 * first), not a plain cancel.
 */
export const EXPENSE_CLAIM_STATUSES = [
  'draft',
  'pending',
  'approved',
  'rejected',
  'settled',
  'withdrawn',
  'cancelled',
] as const;
export type ExpenseClaimStatus = (typeof EXPENSE_CLAIM_STATUSES)[number];

/**
 * An advance's own lifecycle — added for TG-5 (REIMBURSEMENT_PLAN.md), the one task group the
 * first slice deliberately left out. Wider than a claim's own status for the same reason
 * `PAYROLL_LOAN_STATUSES` is wider than `PAYROLL_LOAN_REQUEST_STATUSES`: "the request was approved"
 * and "the money actually moved" are different facts, and conflating them is how a written-off
 * advance ends up looking identical to one nobody ever paid out.
 *
 * `draft` through `withdrawn` is the request half, mirroring `EXPENSE_CLAIM_STATUSES` exactly, so an
 * employee's advance and their claims share one mental model. `disbursed` is set once, at the one
 * finance action that pays it out (`disburseAdvance`). `settled` is reached automatically, never
 * chosen by a caller, the moment `outstandingAmount` nets to zero against real claims. `written_off`
 * is the manual close for an advance nobody is ever going to fully claim against — same reasoning,
 * and the same mandatory-reason requirement, as `PayrollLoan`'s own write-off.
 */
export const EXPENSE_ADVANCE_STATUSES = [
  'draft',
  'pending',
  'approved',
  'rejected',
  'withdrawn',
  'disbursed',
  'settled',
  'written_off',
] as const;
export type ExpenseAdvanceStatus = (typeof EXPENSE_ADVANCE_STATUSES)[number];

/** R-P10 — the "travel desk" half of a `TravelRequest`: a plain status tracker, not a real GDS/
 * booking-system integration (`schema.prisma`'s own model comment has the full reasoning).
 * Settable only once the request itself is `approved` (enforced at the route, not here). */
export const TRAVEL_BOOKING_STATUSES = ['not_booked', 'booked', 'cancelled'] as const;
export type TravelBookingStatus = (typeof TRAVEL_BOOKING_STATUSES)[number];

/**
 * R-P2 (Keka parity) — how a category's `amount` is arrived at. `actual` is a real bill (every
 * category before this phase). `mileage` and `per_diem` compute `amount` server-side from a rate
 * on the category's policy rule (R-P6), rather than the employee typing a total.
 */
export const EXPENSE_CATEGORY_USAGE_TYPES = ['actual', 'mileage', 'per_diem'] as const;
export type ExpenseCategoryUsageType = (typeof EXPENSE_CATEGORY_USAGE_TYPES)[number];

/** The period an `ExpensePolicyCategoryRule` period limit or count limit resets on. */
export const EXPENSE_POLICY_RULE_PERIODS = [
  'day',
  'month',
  'quarter',
  'half_year',
  'year',
] as const;
export type ExpensePolicyRulePeriod = (typeof EXPENSE_POLICY_RULE_PERIODS)[number];

/** Distance unit a mileage rule's rate is quoted per. */
export const EXPENSE_MILEAGE_UNITS = ['km', 'mile'] as const;
export type ExpenseMileageUnit = (typeof EXPENSE_MILEAGE_UNITS)[number];

/**
 * How the employee paid at the time of the expense — not how the company reimburses them, which
 * is a separate `payoutMode` set at settlement. Conflating the two loses the distinction between
 * "paid by personal UPI" and "refunded to their bank account".
 */
/** EXP-P2-15 (Keka parity) — `card` here always meant the employee's OWN card; `corporate_card` is
 * a company-issued card the employee never paid out of pocket on, so settling such a claim must
 * never pay the employee again for money the company already spent — see `settleExpenseClaim`'s
 * own corporate-card branch. */
export const PAID_MODES = [
  'cash',
  'card',
  'upi',
  'net_banking',
  'wallet',
  'corporate_card',
  'other',
] as const;
export type PaidMode = (typeof PAID_MODES)[number];

/**
 * How the COMPANY paid an approved claim back, set once at settlement — never at submit, and never
 * confused with `paidMode` above. `payroll_add_on` records that the amount rides along in the
 * person's next salary run as a line item; it does not require `@hrms/payroll` to exist or run
 * anything, it is only ever a label this module writes onto its own row.
 *
 * `advance` (TG-5) is the one mode `settleClaim` chooses FOR the caller, never something a client
 * may pick freely: when a claim carries an `advanceId` and the advance's remaining balance covers
 * the claim in full, no new money moves at all — the employee was already paid, before the fact,
 * when the advance itself was disbursed. Forcing this mode there rather than letting the caller pick
 * `other` is what keeps Paid history honest about which claims actually triggered a fresh payout and
 * which didn't.
 *
 * `corporate_card` (EXP-P2-15) is the same kind of forced mode, for the same reason: a claim whose
 * `paidMode` is `corporate_card` never has new money move either — the company already paid the
 * card issuer directly, so paying the employee too would be a double-pay, not a reimbursement.
 */
export const PAYOUT_MODES = [
  'bank_transfer',
  'payroll_add_on',
  'cash',
  'corporate_card',
  'advance',
  'other',
] as const;
export type PayoutMode = (typeof PAYOUT_MODES)[number];

/**
 * ADDED 2026-09-17 for payment batches. `draft`: claims selected, nothing generated yet.
 * `file_generated`: the NEFT/RTGS file exists in Spaces, finance has downloaded it and is expected
 * to upload it to the bank next. `paid`: finance confirmed the bank actually moved the money, and
 * every claim in the batch was settled in the same transaction. No route back from `paid` — same
 * ledger-integrity reasoning as `ExpenseClaim.reference` never being reused.
 */
export const EXPENSE_PAYMENT_BATCH_STATUSES = ['draft', 'file_generated', 'paid'] as const;
export type ExpensePaymentBatchStatus = (typeof EXPENSE_PAYMENT_BATCH_STATUSES)[number];

/**
 * ADDED 2026-09-17 — which bank's own bulk NEFT/RTGS upload template `generateBankFile` writes to.
 * Chosen at batch creation (`createPaymentBatch`), stored on the row, and reused on every
 * regeneration — the same batch is always paid through the same bank account, so the file format
 * cannot change mid-batch.
 *
 * COLUMN LAYOUTS ARE COMMONLY-DOCUMENTED PATTERNS, NOT A VERIFIED SPEC FOR ANY SPECIFIC ACCOUNT —
 * see `buildBankFileCsv`'s own header comment in `expense.ts`. Corporate banking products differ by
 * bank AND by tier; confirm against the actual portal before a generated file is ever uploaded for
 * real money.
 */
export const BANK_FILE_FORMATS = ['hdfc', 'icici', 'axis', 'sbi'] as const;
export type BankFileFormat = (typeof BANK_FILE_FORMATS)[number];

// ---------------------------------------------------------------------------
// Payroll
// ---------------------------------------------------------------------------

export const PAY_FREQUENCIES = ['monthly', 'semi_monthly', 'fortnightly', 'weekly'] as const;
export type PayFrequency = (typeof PAY_FREQUENCIES)[number];

/**
 * The payroll run lifecycle. Every transition is decided by the API against the run's current
 * state — the client never sets one of these directly, and the stepper on screen is a rendering of
 * whichever state came back, not a source of truth.
 *
 * `approved` authorises a calculation version for payment PREPARATION. It does not mean anyone has
 * been paid, and it does not close the payroll month: both are separate operations under Payments
 * & Payslips. `reopened` invalidates a prior approval and requires a fresh calculation and a fresh
 * approval before that version can be released again.
 *
 * `finalized` and `released` are ADDED after `approved` (ADR 0014, D1): nothing above was renamed.
 * `finalized` locks the approved calculation version, its results and its rules snapshot; `released`
 * makes the run's outputs (payslips, payments) available. A run keeps `approvedCalculationVersionId`
 * through both, so every reader of it is unaffected.
 */
export const PAYROLL_RUN_STATES = [
  'draft',
  'inputs_locked',
  'calculating',
  'review_ready',
  'submitted',
  'approved',
  'finalized',
  'released',
  'changes_requested',
  'reopened',
  'cancelled',
] as const;
export type PayrollRunState = (typeof PAYROLL_RUN_STATES)[number];

/**
 * One attempt at calculating a run. `superseded` is how a retry or a recalculation retires an
 * older version WITHOUT deleting it: a stale job that finishes late finds its version already
 * superseded and writes nothing, so a failed or obsolete job can never replace the latest
 * successful calculation.
 */
export const PAYROLL_CALCULATION_STATES = [
  'queued',
  'running',
  'ready',
  'failed',
  'superseded',
] as const;
export type PayrollCalculationState = (typeof PAYROLL_CALCULATION_STATES)[number];

/**
 * What a diagnostic BLOCKS — kept separate from how alarming it looks, because they are not the
 * same question. An invalid bank IFSC makes the payment impossible while leaving the salary
 * arithmetic perfectly valid: that is a `payment_blocker`, not a `calculation_blocker`, and it must
 * not stop a run being reviewed and approved.
 */
export const PAYROLL_DIAGNOSTIC_CLASSES = [
  'calculation_blocker',
  'approval_warning',
  'payment_blocker',
] as const;
export type PayrollDiagnosticClass = (typeof PAYROLL_DIAGNOSTIC_CLASSES)[number];

export const PAYROLL_DIAGNOSTIC_SEVERITIES = ['high', 'medium', 'low'] as const;
export type PayrollDiagnosticSeverity = (typeof PAYROLL_DIAGNOSTIC_SEVERITIES)[number];

/**
 * `overridden` means someone with the permission accepted a warning with a recorded reason —
 * the finding stays on the record. `resolved` is only ever written by a re-run of the diagnostic
 * engine finding the condition gone; nothing marks itself resolved.
 */
export const PAYROLL_DIAGNOSTIC_STATES = ['open', 'overridden', 'resolved', 'held'] as const;
export type PayrollDiagnosticState = (typeof PAYROLL_DIAGNOSTIC_STATES)[number];

/** What a MoM variance figure is attributed to. `other` is the reconciling remainder, never a gap. */
export const PAYROLL_VARIANCE_DRIVERS = [
  'joiners',
  'exits',
  'revisions',
  'variable_pay',
  'reimbursements',
  'recoveries',
  'statutory',
  'attendance',
  'other',
] as const;
export type PayrollVarianceDriver = (typeof PAYROLL_VARIANCE_DRIVERS)[number];

// ---------------------------------------------------------------------------
// Statutory compliance & tax documents
// ---------------------------------------------------------------------------

/**
 * A statutory return this system can prepare a file for.
 *
 * ONE ENTRY PER RETURN, NOT PER STATUTORY BODY. Karnataka's monthly PT statement and Maharashtra's
 * are the same return type filed against different registrations — the state lives on the
 * registration, not in this list — while PT's monthly and annual returns are genuinely different
 * documents with different due dates and different contents, so they are two entries.
 *
 * `tds_24q` is quarterly; everything else is monthly. The period a filing covers is stored on the
 * filing row rather than inferred from the type, because a quarterly return is not "three monthly
 * ones" and nothing should try to reconstruct it as such.
 */
export const STATUTORY_RETURN_TYPES = [
  'pf_ecr',
  'esic_monthly',
  'pt_monthly',
  'pt_annual',
  'lwf_periodic',
  'tds_24q',
] as const;
export type StatutoryReturnType = (typeof STATUTORY_RETURN_TYPES)[number];

/**
 * The output specification a filing's file is written to. The FORMAT is a property of the generated
 * file, not of the return: EPFO has published more than one ECR layout, and an entity that has not
 * migrated still needs last year's shape. Recording which one produced a given file is what makes a
 * rejected upload diagnosable.
 */
export const STATUTORY_FILE_FORMATS = [
  'epfo_ecr_v2',
  'esic_monthly_csv',
  'pt_statement_csv',
  'lwf_statement_csv',
  'nsdl_24q_txt',
] as const;
export type StatutoryFileFormat = (typeof STATUTORY_FILE_FORMATS)[number];

/**
 * A filing's lifecycle.
 *
 * `generated` and `signed_off` are deliberately separate, and so are `signed_off` and `remitted`.
 * Generating a file is clerical; authorising it is a named person putting their name to a statutory
 * declaration; remitting is money leaving a bank account. Collapsing any two of those into one
 * state would mean one click did all three, which is precisely what a maker–checker control exists
 * to prevent.
 *
 * `filed` is recorded from the portal acknowledgement, and this system never claims it on its own:
 * nothing here uploads to EPFO, ESIC, or TRACES. A human files, and records what came back.
 */
export const STATUTORY_FILING_STATES = [
  'not_generated',
  'generating',
  'generated',
  'signed_off',
  'remitted',
  'filed',
  'failed',
] as const;
export type StatutoryFilingState = (typeof STATUTORY_FILING_STATES)[number];

/**
 * Why an employee who was in scope did not make it into the file.
 *
 * AN EXCLUDED EMPLOYEE IS A ROW, NOT A GAP. A member missing from an ECR because their UAN is not
 * Aadhaar-seeded is the single most common cause of a rejected bulk upload, and the useful artefact
 * is a named list HR can act on — not a smaller file and a silent difference between the filing
 * total and the payroll total.
 */
export const STATUTORY_EXCLUSION_REASONS = [
  'uan_not_seeded',
  'uan_missing',
  'esic_ip_missing',
  'pan_missing',
  'pan_invalid',
  'registration_missing',
  'not_applicable',
  'zero_contribution',
] as const;
export type StatutoryExclusionReason = (typeof STATUTORY_EXCLUSION_REASONS)[number];

/**
 * A Form 16 certificate's lifecycle for one employee and one fiscal year.
 *
 * `generated` means Part B exists in this system. `issued` means it has been released to the
 * employee. Part A comes from TRACES and is uploaded, never generated here — a Part A this system
 * produced itself would be a forgery of a government certificate.
 */
export const FORM16_STATES = ['not_generated', 'generated', 'issued', 'revoked'] as const;
export type Form16State = (typeof FORM16_STATES)[number];

// ---------------------------------------------------------------------------
// Payroll accounting & reporting
// ---------------------------------------------------------------------------

/**
 * The account groups a payroll journal posts to. Deliberately a SEMANTIC list, not a chart of
 * accounts: the customer's own account codes are mapped onto these per legal entity, because every
 * finance team numbers their ledger differently and none of them will renumber it for us.
 */
export const PAYROLL_GL_ACCOUNT_GROUPS = [
  'gross_salary_expense',
  'employer_statutory_expense',
  'reimbursement_expense',
  'bank_disbursement_clearing',
  'statutory_payables',
  /** Employer cost that is NOT owed to a statutory body this month: the gratuity provision, employer-paid benefits (B19). */
  'employer_provisions',
  'tax_payable',
  'recoveries_receivable',
  'other',
] as const;
export type PayrollGlAccountGroup = (typeof PAYROLL_GL_ACCOUNT_GROUPS)[number];

/**
 * A journal batch's lifecycle. `ready` means it balances and every account group used has a mapped
 * code; an unbalanced or unmapped batch stays `draft` and says why. `posted` records that the ERP
 * accepted it, with its reference — this system posts through an export or a connector, and never
 * claims a posting it did not get an acknowledgement for.
 */
export const PAYROLL_GL_POSTING_STATES = [
  'draft',
  'ready',
  'posted',
  'failed',
  'reversed',
] as const;
export type PayrollGlPostingState = (typeof PAYROLL_GL_POSTING_STATES)[number];

/**
 * An accounting period's state, which is a FINANCE fact and not the payroll run's state.
 *
 * `locked_preliminary` is the interesting one: the run is approved and the numbers are stable
 * enough to report on, but the month is not closed — arrears, a reversed payment or a late
 * settlement can still land. `closed` means no further payroll accounting entry may be booked into
 * the period, and a correction must go to a later one.
 */
export const PAYROLL_PERIOD_CLOSE_STATES = [
  'open',
  'locked_preliminary',
  'closed',
  'reopened',
] as const;
export type PayrollPeriodCloseState = (typeof PAYROLL_PERIOD_CLOSE_STATES)[number];

/** The exportable registers. Each is a projection of persisted results — none recalculates. */
export const PAYROLL_REPORT_KINDS = [
  'salary_register',
  'statutory_register',
  'department_allocation',
  'variance',
  'headcount_cost',
  'bank_batch',
  'ytd_summary',
] as const;
export type PayrollReportKind = (typeof PAYROLL_REPORT_KINDS)[number];

// ---------------------------------------------------------------------------
// Salary queries
// ---------------------------------------------------------------------------

/**
 * What an employee says is wrong. Drives routing and the SLA clock, so it is a fixed list rather
 * than free text — "my TDS looks too high" and "my reimbursement is missing" go to different desks
 * and carry different turnaround expectations.
 */
export const SALARY_QUERY_TYPES = [
  'tds_statutory',
  'payslip_discrepancy',
  'attendance_lop',
  'reimbursement',
  'arrears_revision',
  'bank_payment',
  'loan_advance',
  'other',
] as const;
export type SalaryQueryType = (typeof SALARY_QUERY_TYPES)[number];

/**
 * A query's lifecycle.
 *
 * `awaiting_employee` stops the SLA clock and nothing else does — a desk waiting on the person who
 * raised the ticket is not a desk that is late. `resolved` is the desk's answer; `closed` is the
 * employee accepting it or the auto-close window expiring. Keeping them apart is what makes
 * "resolved but disputed" expressible instead of reopening a closed ticket.
 */
export const SALARY_QUERY_STATES = [
  'open',
  'in_progress',
  'awaiting_employee',
  'resolved',
  'closed',
  'rejected',
] as const;
export type SalaryQueryState = (typeof SALARY_QUERY_STATES)[number];

export const SALARY_QUERY_PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const;
export type SalaryQueryPriority = (typeof SALARY_QUERY_PRIORITIES)[number];

/**
 * A message on a query's thread.
 *
 * `internal_note` IS NEVER SHOWN TO THE EMPLOYEE, and that is enforced by the API filtering the
 * thread per caller — not by the client choosing not to render it. A desk discussing whether
 * someone's claim holds up needs somewhere to do it, and the moment that somewhere is only
 * client-side hidden it is a leak waiting for a curious network tab.
 */
export const SALARY_QUERY_MESSAGE_KINDS = [
  'employee_note',
  'internal_note',
  'official_response',
] as const;
export type SalaryQueryMessageKind = (typeof SALARY_QUERY_MESSAGE_KINDS)[number];

/** How a query ended. Recorded on resolution, and what the SLA report is grouped by. */
export const SALARY_QUERY_RESOLUTIONS = [
  'explained',
  'adjustment_scheduled',
  'corrected_in_run',
  'no_discrepancy',
  'duplicate',
  'rejected',
] as const;
export type SalaryQueryResolution = (typeof SALARY_QUERY_RESOLUTIONS)[number];

// ---------------------------------------------------------------------------
// Income-tax declarations & proofs
// ---------------------------------------------------------------------------

/**
 * A declaration revision's lifecycle. A revision is immutable once `submitted`: an employee who
 * wants to correct something gets a NEW revision, and the previous revision keeps its reviews.
 *
 * The three terminal review outcomes are recorded per declaration from its items' reviews:
 * everything accepted in full, some accepted, or nothing accepted.
 */
export const TAX_DECLARATION_STATES = [
  'draft',
  'submitted',
  'under_review',
  'verified',
  'partially_verified',
  'disallowed',
  'clarification_required',
] as const;
export type TaxDeclarationState = (typeof TAX_DECLARATION_STATES)[number];

/** One reviewer's decision on one declaration item. */
export const TAX_PROOF_DECISIONS = [
  'accepted',
  'partially_accepted',
  'disallowed',
  'clarification_required',
] as const;
export type TaxProofDecision = (typeof TAX_PROOF_DECISIONS)[number];

/**
 * How a declaration category's claimable amount is worked out, so the engine knows which of an
 * item's inputs matter. The CAPS AND RATES are not here — they live in the versioned tax-year
 * configuration, because they change by statute every year and a constant in code cannot be
 * reviewed or effective-dated.
 */
export const TAX_CATEGORY_CALCULATIONS = [
  /** Declared amount, subject to the category's own cap and its section's aggregate cap. */
  'declared_amount',
  /** Section 10(13A): the least of actual HRA, rent paid less 10% of salary, and the metro rate. */
  'hra_exemption',
  /** Section 24(b): interest on a housing loan, capped for a self-occupied property. */
  'home_loan_interest',
  /** Employer-routed, already on the payslip — evidence is the payroll record, not a receipt. */
  'payroll_sourced',
] as const;
export type TaxCategoryCalculation = (typeof TAX_CATEGORY_CALCULATIONS)[number];

/**
 * Where a `payroll_sourced` category's amount is read from when payroll is the record (B31): the employee's own
 * provident fund (Section 80C) and the professional tax deducted (Section 16(iii)). A category with a source is never
 * declared by the employee; the engine reads the year's figure from payroll.
 */
export const TAX_PAYROLL_SOURCES = ['employee_provident_fund', 'professional_tax'] as const;
export type TaxPayrollSource = (typeof TAX_PAYROLL_SOURCES)[number];

export const COMPONENT_TYPES = [
  'earning',
  'deduction',
  'employer_contribution',
  'reimbursement',
  'perquisite',
  'informational',
] as const;
export type ComponentType = (typeof COMPONENT_TYPES)[number];

/** Indian income-tax regime. The employee elects one per financial year. */
export const TAX_REGIMES = ['old', 'new'] as const;
export type TaxRegime = (typeof TAX_REGIMES)[number];

/**
 * How a legal entity's corporate identifier is issued. A private limited company has a CIN, an LLP
 * an LLPIN — enforcing the CIN format on every entity would reject a perfectly valid LLP.
 */
export const CORPORATE_ID_TYPES = ['cin', 'llpin', 'other'] as const;
export type CorporateIdType = (typeof CORPORATE_ID_TYPES)[number];

/** The statutory schemes a legal entity configures, each as its own effective-dated rule. */
export const STATUTORY_RULE_TYPES = ['pf', 'esi', 'pt', 'lwf', 'gratuity', 'bonus'] as const;
export type StatutoryRuleType = (typeof STATUTORY_RULE_TYPES)[number];

/**
 * The employee-facing application, from first submit to a final decision. Distinct from
 * `PAYROLL_LOAN_STATUSES` below — an application being `approved` only means the money is
 * authorised; disbursement and recovery are tracked separately on the `PayrollLoan` row itself, per
 * the same "approving isn't paying" split `EXPENSE_CLAIM_STATUSES` already uses.
 */
export const PAYROLL_LOAN_REQUEST_STATUSES = [
  'draft',
  'submitted',
  'recommended',
  'declined_by_manager',
  'approved',
  'rejected',
  'withdrawn',
] as const;
export type PayrollLoanRequestStatus = (typeof PAYROLL_LOAN_REQUEST_STATUSES)[number];

/**
 * The confirmed loan's own lifecycle, starting only once a request is `approved`. This enum is
 * coarse and display-oriented on purpose — the actual payout fact lives in `disbursedAt`, and the
 * actual repayment fact lives in `outstandingPrincipal`, so the two are never confused by reading
 * this status alone.
 */
export const PAYROLL_LOAN_STATUSES = [
  'pending_disbursement',
  'active',
  'settled',
  'written_off',
] as const;
export type PayrollLoanStatus = (typeof PAYROLL_LOAN_STATUSES)[number];

/** One instalment's own recovery state, tracked per cycle so a reversed payroll posting is visible. */
export const PAYROLL_LOAN_INSTALLMENT_STATUSES = [
  'scheduled',
  'recovered',
  'reversed',
  'waived',
] as const;
export type PayrollLoanInstallmentStatus = (typeof PAYROLL_LOAN_INSTALLMENT_STATUSES)[number];

/** A one-time earning or deduction folded into a specific run — never a recurring component. */
export const PAYROLL_VARIABLE_INPUT_CATEGORIES = [
  'bonus_taxable',
  'bonus_non_taxable',
  'incentive',
  'overtime',
  'arrears',
  'one_time_deduction',
  /**
   * Extra income tax the employer deducts this month on top of the calculated TDS (an employee's request, e.g. to cover
   * other income). It is TDS — in the run's tax deducted, Form 16 and 24Q — but it does not reduce the calculated
   * TDS of later months; the regular calculation still spreads the whole year's tax.
   */
  'additional_tds',
] as const;
export type PayrollVariableInputCategory = (typeof PAYROLL_VARIABLE_INPUT_CATEGORIES)[number];

/**
 * An approved `ExpenseClaim` reserved onto a specific payroll run. Distinct from the claim's own
 * `status` (still `approved` throughout) and from `payoutMode: 'payroll_add_on'` (a label the
 * expense module writes on itself) — this is payroll's own reservation, so the same claim can never
 * be paid twice through two different paths. See `payroll-inputs.contract.ts`.
 */
export const PAYROLL_EXPENSE_INCLUSION_STATUSES = [
  'reserved',
  'included',
  'settled',
  'released',
] as const;
export type PayrollExpenseInclusionStatus = (typeof PAYROLL_EXPENSE_INCLUSION_STATUSES)[number];

/**
 * An EWA withdrawal's payout, kept honest about what is and is not known. `reserved`: the amount is
 * held against the employee's limit but nothing has been sent. `payout_pending`: sent to the
 * provider, no answer yet. `disbursed`: the provider confirmed. `failed`: the provider definitively
 * refused it — the ONLY state that gives the reservation back. `unknown`: the call timed out or
 * came back unreadable; the money may or may not have left, so the reservation stays and a person
 * reconciles it rather than the system guessing and re-sending.
 */
export const EWA_WITHDRAWAL_STATUSES = [
  'reserved',
  'payout_pending',
  'disbursed',
  'failed',
  'unknown',
] as const;
export type EwaWithdrawalStatus = (typeof EWA_WITHDRAWAL_STATUSES)[number];

/** The recovery obligation a confirmed EWA payout creates, taken out of that cycle's pay. */
export const EWA_RECOVERY_STATUSES = ['none', 'due', 'recovered', 'reversed'] as const;
export type EwaRecoveryStatus = (typeof EWA_RECOVERY_STATUSES)[number];

/** How a monthly pay date is derived from the calendar. */
export const PAY_DAY_RULES = ['last_working_day', 'last_day', 'fixed_day'] as const;
export type PayDayRule = (typeof PAY_DAY_RULES)[number];

/** What happens when a pay date lands on a weekend or holiday. */
export const HOLIDAY_ADJUSTMENTS = ['none', 'previous_working_day'] as const;
export type HolidayAdjustment = (typeof HOLIDAY_ADJUSTMENTS)[number];

/**
 * Where a cycle sits in time. `processed` is only ever read from a payroll run, never inferred from
 * the date — a pay date in the past with no run behind it is `awaiting_processing`, not processed.
 */
export const PAY_CYCLE_STATES = ['processed', 'active', 'awaiting_processing', 'upcoming'] as const;
export type PayCycleState = (typeof PAY_CYCLE_STATES)[number];

/** How a salary component's monthly amount is derived. */
export const COMPONENT_CALCULATION_TYPES = [
  'percent_of_ctc',
  'percent_of_basic',
  'fixed_monthly',
  'balancing',
  'statutory',
] as const;
export type ComponentCalculationType = (typeof COMPONENT_CALCULATION_TYPES)[number];

export const COMPONENT_TAXABILITIES = [
  'taxable',
  'exempt',
  'partially_exempt',
  'non_taxable',
] as const;
export type ComponentTaxability = (typeof COMPONENT_TAXABILITIES)[number];

/** `pending_approval` is a component proposed but not yet approved — never used in a calculation. */
export const SALARY_COMPONENT_STATUSES = ['active', 'inactive', 'pending_approval'] as const;
export type SalaryComponentStatus = (typeof SALARY_COMPONENT_STATUSES)[number];

export const COMPONENT_CHANGE_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type ComponentChangeStatus = (typeof COMPONENT_CHANGE_STATUSES)[number];

// ---------------------------------------------------------------------------
// Recruitment
// ---------------------------------------------------------------------------

/** A headcount request's approval lifecycle — mirrors a `WorkflowInstance`, never written by hand. */
export const REQUISITION_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'rejected',
  'closed',
] as const;
export type RequisitionStatus = (typeof REQUISITION_STATUSES)[number];

export const JOB_POSTING_STATUSES = ['active', 'paused', 'closed'] as const;
export type JobPostingStatus = (typeof JOB_POSTING_STATUSES)[number];

/**
 * The Kanban a candidate moves through. `hired` and `rejected` are terminal — the candidate's
 * `status` field (below), not this one, records whether they withdrew instead of being moved here.
 */
export const CANDIDATE_STAGES = [
  'applied',
  'screening',
  'interview',
  'assessment',
  'offer',
  'hired',
  'rejected',
] as const;
export type CandidateStage = (typeof CANDIDATE_STAGES)[number];

/** Independent of `stage`: a candidate can withdraw from any stage without a recruiter rejecting them. */
export const CANDIDATE_STATUSES = ['active', 'withdrawn'] as const;
export type CandidateStatus = (typeof CANDIDATE_STATUSES)[number];

export const CANDIDATE_SOURCES = [
  'career_site',
  'linkedin',
  'indeed',
  'employee_referral',
  // An employee applying to an internal posting themselves.
  'internal',
  // Where a careers link was shared (`?source=` on the apply link) or found.
  'naukri',
  'google_jobs',
  'social',
  // Submitted by a recruitment agency through its portal link.
  'agency',
  'direct',
  'other',
] as const;
export type CandidateSource = (typeof CANDIDATE_SOURCES)[number];

/** Self-reported by the candidate on the public apply form — recruiter-facing context only, never
 *  used to auto-filter or score an applicant. */
export const EXPERIENCE_LEVELS = ['entry', 'mid', 'senior', 'lead'] as const;
export type ExperienceLevel = (typeof EXPERIENCE_LEVELS)[number];

export const INTERVIEW_STATUSES = ['scheduled', 'completed', 'cancelled', 'no_show'] as const;
export type InterviewStatus = (typeof INTERVIEW_STATUSES)[number];

export const INTERVIEW_RECOMMENDATIONS = ['strong_hire', 'hire', 'neutral', 'no_hire'] as const;

/**
 * A person's part in hiring for one requisition (Keka's hiring "stakeholders"). Membership gives
 * access to that requisition's candidates without an org-wide recruitment role — gap analysis bug
 * 11: a hiring manager's reporting-tree scope never matched a candidate, so they saw nothing.
 * `interviewer` here is a standing panel member; anyone put on one interview also sees that
 * candidate, through the interview itself.
 */
export const HIRING_TEAM_ROLES = ['hiring_manager', 'recruiter', 'coordinator', 'interviewer'] as const;
export type HiringTeamRole = (typeof HIRING_TEAM_ROLES)[number];

/** What the candidate timeline records (recruitment R4). Append-only; one row per thing that happened. */
export const CANDIDATE_ACTIVITY_KINDS = [
  'applied',
  'added',
  'referred',
  'stage_changed',
  'rejected',
  'reconsidered',
  'withdrawn',
  'note',
  'email',
  'interview_scheduled',
  'interview_rescheduled',
  'interview_cancelled',
  'scorecard_filed',
  'assessment_sent',
  'assessment_submitted',
  'offer_created',
  'offer_sent',
  'offer_accepted',
  'offer_declined',
  'offer_rescinded',
  'rating_set',
  // A screening question flagged or auto-rejected the application.
  'screening',
  'check_updated',
  'linked',
  'did_not_join',
] as const;
export type CandidateActivityKind = (typeof CANDIDATE_ACTIVITY_KINDS)[number];

/** An email to a candidate: waiting for its send time, sent, refused by the mail server, or not
 *  attempted (no address, test domain, no mail relay configured). */
export const CANDIDATE_EMAIL_STATUSES = ['scheduled', 'sent', 'failed', 'skipped'] as const;
export type CandidateEmailStatus = (typeof CANDIDATE_EMAIL_STATUSES)[number];

export const CANDIDATE_EMAIL_KINDS = ['message', 'rejection'] as const;
export type CandidateEmailKind = (typeof CANDIDATE_EMAIL_KINDS)[number];

/** `next_morning` queues the email for 10:00 the next day, in the legal entity's own timezone —
 *  rejections landing in the evening feel colder than the same email the next morning. */
export const EMAIL_TIMINGS = ['now', 'next_morning'] as const;
export type EmailTiming = (typeof EMAIL_TIMINGS)[number];

/** Scorecard rating scales (Keka: 5-point by default, or 3-point). */
export const SCORECARD_SCALES = [3, 5] as const;
export type ScorecardScale = (typeof SCORECARD_SCALES)[number];
export type InterviewRecommendation = (typeof INTERVIEW_RECOMMENDATIONS)[number];

/**
 * How an interview's `meetingUrl` was produced. `manual` is the only provider actually wired today
 * — a recruiter pastes a link. `google_meet`/`ms_teams` are reserved for when an org connects real
 * OAuth credentials (Settings -> Integrations); see @hrms/ai... no, see apps/api's calendar
 * provider interface. Persisting the provider now means no migration is needed when that lands.
 */
export const MEETING_PROVIDERS = ['manual', 'google_meet', 'ms_teams'] as const;
export type MeetingProvider = (typeof MEETING_PROVIDERS)[number];

export const OFFER_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'sent',
  'accepted',
  'declined',
  'rescinded',
  // A newer version of the offer replaced this one (a revision). Its link stops working.
  'superseded',
  // Accepted, then never turned up. The seat is open again.
  'did_not_join',
  // Never stored: a `sent` offer past its `expiresAt` reads as this (same compute-at-read posture
  // as slot offers), so no background job has to keep a column in sync.
  'expired',
] as const;
export type OfferStatus = (typeof OFFER_STATUSES)[number];

/** Where an offer's document comes from — the existing {{TOKEN}}-rendered template flow, or a
 *  recruiter-supplied PDF. */
export const OFFER_SOURCE_TYPES = ['template', 'upload'] as const;
export type OfferSourceType = (typeof OFFER_SOURCE_TYPES)[number];

/** The two providers Settings -> Integrations offers to connect — see CalendarIntegration. */
export const CALENDAR_PROVIDERS = ['google', 'microsoft'] as const;
export type CalendarProvider = (typeof CALENDAR_PROVIDERS)[number];

export const INTEGRATION_STATUSES = ['active', 'revoked', 'error'] as const;
export type IntegrationStatus = (typeof INTEGRATION_STATUSES)[number];

/** A recruiter-authored "pick a slot" link sent to a candidate. */
export const SLOT_OFFER_STATUSES = ['open', 'booked', 'expired', 'cancelled'] as const;
export type SlotOfferStatus = (typeof SLOT_OFFER_STATUSES)[number];

/** pending (amount set, editable) -> pending_approval (workflow) -> approved | rejected -> paid.
 *  A rejected bonus can be corrected and resubmitted. */
export const REFERRAL_BONUS_STATUSES = [
  'pending',
  'pending_approval',
  'approved',
  'rejected',
  'paid',
] as const;
export type ReferralBonusStatus = (typeof REFERRAL_BONUS_STATUSES)[number];

/** A template question's answer shape. `multiple_choice` is the only one `options` applies to. */
export const ASSESSMENT_QUESTION_TYPES = ['text', 'multiple_choice', 'rating'] as const;
export type AssessmentQuestionType = (typeof ASSESSMENT_QUESTION_TYPES)[number];

/** One `AssessmentTemplate` sent to one candidate. */
export const ASSESSMENT_STATUSES = ['sent', 'submitted', 'expired'] as const;
export type AssessmentStatus = (typeof ASSESSMENT_STATUSES)[number];

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export const CONVERSATION_TYPES = ['dm', 'group', 'channel'] as const;
export type ConversationType = (typeof CONVERSATION_TYPES)[number];

export const MESSAGE_KINDS = ['text', 'file', 'system', 'ai', 'approval_card'] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

// ---------------------------------------------------------------------------
// AI
// ---------------------------------------------------------------------------

/**
 * The unit of AI routing. Effort, not a model id — an effort request can always be satisfied by
 * whatever providers are actually configured and funded, whereas a model id is a promise about one
 * vendor's billing status.
 */
export const AI_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type AiEffort = (typeof AI_EFFORTS)[number];

export const AI_TOOL_KINDS = ['read', 'write'] as const;
export type AiToolKind = (typeof AI_TOOL_KINDS)[number];

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export const NOTIFICATION_CHANNELS = [
  'in_app',
  'push',
  'email',
  'sms',
  'whatsapp',
  'web_push',
  'slack',
] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

// ---------------------------------------------------------------------------
// Projects & delivery
// ---------------------------------------------------------------------------

export const PROJECT_STATUSES = [
  'draft',
  'active',
  'on_hold',
  'completed',
  'cancelled',
  'archived',
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_BILLING_MODELS = [
  'non_billable',
  'time_and_materials',
  'fixed_price',
  'milestone',
] as const;
export type ProjectBillingModel = (typeof PROJECT_BILLING_MODELS)[number];

/** A `ProjectMember`'s role on that one project — distinct from the org-wide `Role`. */
export const PROJECT_ROLES = ['manager', 'lead', 'member', 'viewer'] as const;
export type ProjectRole = (typeof PROJECT_ROLES)[number];

/** The six built-in `TaskStatusDefinition` keys, seeded for every org (ADR 0020 §6). No longer the
 * exhaustive set of legal `ProjectTask.status` values — see `TaskStatus` below — but still the
 * defaults referenced at task/type creation and in fixtures. */
export const TASK_STATUSES = [
  'todo',
  'in_progress',
  'blocked',
  'in_review',
  'done',
  'cancelled',
] as const;
/** ADR 0020 §6: widened from `(typeof TASK_STATUSES)[number]` to `string` — a task's status is any
 * ACTIVE `TaskStatusDefinition.key` for its org, not just the six built-ins. Code that specifically
 * means one of the six (the evidence check, the clock-out overdue gate, `reopenTask`) keeps comparing
 * against the literal string directly, which remains valid; code that needs "is this task done" in
 * a way that also covers a custom status reads that status's BUCKET instead (see
 * `TaskStatusBucket`), never this type. */
export type TaskStatus = string;

export const TASK_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

/**
 * Daily Work Planning Phase 1 — how a `ProjectTask` came to exist. `manager_assigned`: created
 * through `createTask` by someone with `project.task:manage`. `self_added`: created from an
 * approved check-in item (`workform.ts`'s `createTaskForApprovedItem`). `carried_forward`: the
 * SAME task row, moved to a new day by the Phase 7 carry-forward action (not yet implemented —
 * nothing sets this value yet, but the field and its UI are wired ahead of that phase). `null` on
 * `ProjectTask.originKind` means a pre-existing task from before this column existed — deliberately
 * left unknown, never backfill-guessed (implementation plan Decision 3).
 */
export const TASK_ORIGIN_KINDS = ['manager_assigned', 'self_added', 'carried_forward'] as const;
export type TaskOriginKind = (typeof TASK_ORIGIN_KINDS)[number];

export const MILESTONE_STATUSES = [
  'planned',
  'in_progress',
  'done',
  'slipped',
  'cancelled',
] as const;
export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number];

/** PM-005 (Keka gap closure, follow-up round 3) — `ProjectPhase.status`. Deliberately a plain
 * 3-value bucket-style enum, not the 5-value `MILESTONE_STATUSES` scheme and not a foreign key into
 * `TaskStatusDefinition` (org-customisable per-task-status; a phase is a much coarser grouping). */
export const PHASE_STATUSES = ['not_started', 'in_progress', 'completed'] as const;
export type PhaseStatus = (typeof PHASE_STATUSES)[number];

/** PSA-standard split: a `soft` booking is pipeline (shows on the heatmap, doesn't consume capacity
 * the way `committed` does). */

export const TIMESHEET_STATUSES = ['draft', 'submitted', 'approved', 'rejected', 'locked'] as const;
export type TimesheetStatus = (typeof TIMESHEET_STATUSES)[number];

export const TIMESHEET_PERIOD_TYPES = ['weekly', 'biweekly', 'semi_monthly', 'monthly'] as const;
export type TimesheetPeriodType = (typeof TIMESHEET_PERIOD_TYPES)[number];

/** How a `TimeEntry` was created. `attendance_log` is the verification breadcrumb, filtered out of
 * every project rollup by this value — see ADR 0008. */
/**
 * `checkout`: prefilled from the day's check-out report (plan 4.5) and still the report's to update; the moment the
 * employee edits it, it becomes `timesheet`. `task`: logged with the "Log time" button on a task.
 */
export const TIME_ENTRY_SOURCES = [
  'timesheet',
  'attendance_log',
  'timer',
  'import',
  'checkout',
  'task',
] as const;
export type TimeEntrySource = (typeof TIME_ENTRY_SOURCES)[number];

/** How strictly logged project time is held to attendance (plan 6.6). `warn` is the default. */
export const ATTENDANCE_CHECK_MODES = ['off', 'warn', 'block'] as const;
export type AttendanceCheckMode = (typeof ATTENDANCE_CHECK_MODES)[number];

// ---------------------------------------------------------------------------
// Check-in / check-out forms (ADR 0009)
// ---------------------------------------------------------------------------

/** The two HR-configured forms an employee fills around a punch. */
export const WORK_FORM_KINDS = ['check_in', 'check_out'] as const;
export type WorkFormKind = (typeof WORK_FORM_KINDS)[number];

/**
 * HR's switch for one form: `off` = no form; `optional` = offered at the punch and skippable;
 * `required` = the punch waits until it has been submitted.
 */
export const WORK_FORM_MODES = ['off', 'optional', 'required'] as const;
export type WorkFormMode = (typeof WORK_FORM_MODES)[number];

/**
 * What happens to a task submitted on the check-in form (plan 4.6, D15). `auto`: it becomes a project task at
 * once, no approval. `manager`: each task needs its own approval from the reporting manager first.
 */
export const WORK_FORM_APPROVAL_MODES = ['auto', 'manager'] as const;
export type WorkFormApprovalMode = (typeof WORK_FORM_APPROVAL_MODES)[number];

export const WORK_FORM_FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'date',
  'select',
  'checkbox',
] as const;
export type WorkFormFieldType = (typeof WORK_FORM_FIELD_TYPES)[number];

/**
 * Presets whose submitted value is written to a real `ProjectTask` column instead of the free-form
 * `custom_fields` bag. Anything not marked with one of these is stored as a custom field.
 */
export const WORK_FORM_SYSTEM_FIELDS = [
  'priority',
  'due_date',
  'estimate_hours',
  'description',
] as const;
export type WorkFormSystemField = (typeof WORK_FORM_SYSTEM_FIELDS)[number];

/** Approval state of one task an employee proposed at check-in. */
export const WORK_PLAN_ITEM_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type WorkPlanItemStatus = (typeof WORK_PLAN_ITEM_STATUSES)[number];

/**
 * Why `assertClockOutAllowed` blocked an `out` punch (ADR 0019). One `out` attempt can surface
 * several of these at once — every unmet condition is reported together, not one-at-a-time.
 */
export const CLOCK_OUT_BLOCK_REASON_CODES = [
  'missing_eod_report',
  'overdue_task',
  'mandatory_learning',
] as const;
export type ClockOutBlockReasonCode = (typeof CLOCK_OUT_BLOCK_REASON_CODES)[number];

/** What the client does to resolve a clock-out blocker — each maps to a real, navigable screen. */
export const CLOCK_OUT_RESOLUTION_ACTION_TYPES = [
  'open_check_out_form',
  'open_task',
  'open_learning_assignment',
] as const;
export type ClockOutResolutionActionType = (typeof CLOCK_OUT_RESOLUTION_ACTION_TYPES)[number];

/** Progress of one employee against one assigned learning item (ADR 0019). */
export const LEARNING_ASSIGNMENT_STATUSES = [
  'assigned',
  'in_progress',
  'completed',
  'waived',
  // Replaced by a training course assignment ("Turn into a course", training redesign §9.2). Closed:
  // it no longer blocks clock-out; `convertedToAssignmentId` points at the course assignment.
  'converted',
] as const;
export type LearningAssignmentStatus = (typeof LEARNING_ASSIGNMENT_STATUSES)[number];

/** Decision state of a request to waive today's clock-out block entirely (ADR 0020 §1). */
export const CLOCK_OUT_OVERRIDE_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type ClockOutOverrideStatus = (typeof CLOCK_OUT_OVERRIDE_STATUSES)[number];

/** Which bucket a `TaskStatusDefinition` maps onto (ADR 0020 §6) — the thing every "is this task
 * finished/cancelled" check actually reads, so a custom status behaves correctly everywhere without
 * each call site needing to know it exists. */
export const TASK_STATUS_BUCKETS = [
  'not_started',
  'in_progress',
  'review',
  'completed',
  'cancelled',
] as const;
export type TaskStatusBucket = (typeof TASK_STATUS_BUCKETS)[number];

// ---------------------------------------------------------------------------
// Performance — Goals & OKRs
// ---------------------------------------------------------------------------

/**
 * The cascade tier an `Objective` sits at. Only two values — "team" from an earlier draft was
 * dropped to match exactly the three requested display tiers: company, department ("branch"),
 * individual (the last of which is a `Goal`, not an `Objective` — see the Goals & OKRs plan).
 */
/** PRF-17: 'team' sits between 'department' and an individual Goal — a manager's own slice of
 *  their department's cascade, scoped by `Objective.ownerPersonId`'s reporting line (there is no
 *  first-class Team model; "team" means the same thing here it does everywhere else in this
 *  codebase — a manager and their direct reports, resolved from the org chart). */
export const GOAL_LEVELS = ['company', 'department', 'team'] as const;
export type GoalLevel = (typeof GOAL_LEVELS)[number];

/** A `Goal`'s lifecycle. `pending_approval` only appears when a `goal` `WorkflowDefinition` exists
 * for the org — see the Goals & OKRs plan's conditional-workflow design. */
export const GOAL_STATUSES = [
  'draft',
  'pending_approval',
  'active',
  'at_risk',
  'achieved',
  'missed',
  'cancelled',
] as const;
export type GoalStatus = (typeof GOAL_STATUSES)[number];

/** How a `KeyResult`/`GoalKeyResult` is measured. `currency` requires a `currency` alongside its
 * amount, per CLAUDE.md invariant 2 — never an amount without its currency. */
export const GOAL_MEASURE_TYPES = [
  'percentage',
  'numeric',
  'currency',
  'boolean',
  'milestone',
] as const;
export type GoalMeasureType = (typeof GOAL_MEASURE_TYPES)[number];

/** Self-reported sentiment on a `GoalCheckIn`, independent of the numeric `progressPercent`. */
export const GOAL_CONFIDENCE = ['on_track', 'at_risk', 'off_track'] as const;
export type GoalConfidence = (typeof GOAL_CONFIDENCE)[number];

/** Who/what produced a `GoalCheckIn` — `ai_assisted` is what makes an AI-drafted narrative
 * auditable, and is what `ApprovalCard`'s `proposedByAi` prop renders from. */
export const GOAL_CHECKIN_SOURCES = ['self', 'manager', 'ai_assisted'] as const;
export type GoalCheckInSource = (typeof GOAL_CHECKIN_SOURCES)[number];

export const OBJECTIVE_STATUSES = ['draft', 'active', 'archived'] as const;
export type ObjectiveStatus = (typeof OBJECTIVE_STATUSES)[number];

// ---------------------------------------------------------------------------
// Performance — Feedback & 1:1s
// ---------------------------------------------------------------------------

/** A `Feedback` row's kind. `request_response` is what a respondent's answer to a `FeedbackRequest`
 * is stored as; `review_peer` is what Section 4's 360° peer-review step reuses this table for. */
export const FEEDBACK_KINDS = [
  'praise',
  'constructive',
  'request_response',
  'review_peer',
] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

/** `public` is the Recognition-Wall-equivalent for feedback; `manager_shared` is what a manager's
 * "view team feedback" reads; `private` is visible only to the subject (plus identity-read roles). */
export const FEEDBACK_VISIBILITIES = ['private', 'manager_shared', 'public'] as const;
export type FeedbackVisibility = (typeof FEEDBACK_VISIBILITIES)[number];

/** How a `FeedbackRequest` respondent relates to the subject — purely descriptive, carries no
 * authorisation weight of its own (that's `feedback.request:create`/`feedback:give`).
 *
 * `skip_level` (PRF-13): the respondent reports to one of the subject's own direct reports (a
 * "grand-report") — the peer-nomination flow's skip-level reviewer type. An UPWARD review (a
 * direct report reviewing their own manager) reuses the existing `direct_report` value rather
 * than adding a fifth one — `direct_report` already means exactly "the respondent is a direct
 * report of the subject", which is precisely the upward-review direction. */
export const FEEDBACK_RELATIONSHIPS = [
  'peer',
  'manager',
  'direct_report',
  'skip_level',
  'cross_functional',
  'external',
] as const;
export type FeedbackRelationship = (typeof FEEDBACK_RELATIONSHIPS)[number];

/** PRF-13 anonymity rule: feedback flowing UP the reporting line (a direct report reviewing their
 * manager, or a grand-report reviewing a skip-level manager) carries real power-dynamic risk, so
 * it is always anonymous — never the respondent's choice. Every other relationship keeps today's
 * behaviour: `Feedback.isAnonymous` is whatever the respondent picks at submission time. */
export function isAnonymityMandatory(relationship: FeedbackRelationship): boolean {
  return relationship === 'direct_report' || relationship === 'skip_level';
}

/** A `FeedbackRequest`'s lifecycle. `expired` is reserved for a future SLA sweep — nothing sets it
 * yet, same as `LEAVE_REQUEST_STATUSES`' `withdrawn` before cancellation logic existed. */
export const FEEDBACK_REQUEST_STATUSES = ['pending', 'submitted', 'declined', 'expired'] as const;
export type FeedbackRequestStatus = (typeof FEEDBACK_REQUEST_STATUSES)[number];

/** A `Recognition` row's visibility — same three-value shape as `FEEDBACK_VISIBILITIES`, kept as its
 * own enum because the two rows mean different things product-wise (a kudos post vs. a private
 * constructive note) even though the wire values match today. */
export const RECOGNITION_VISIBILITIES = ['private', 'manager_shared', 'public'] as const;
export type RecognitionVisibility = (typeof RECOGNITION_VISIBILITIES)[number];

/** `RecognitionPointsBalance.reason` / `RecognitionPointsLedgerEntry.type` (R-PRF-25) — what
 * produced a balance/ledger row. `redemption` is the one value the ledger has that the balance's
 * own inline reason list predates; both read this same vocabulary. */
export const RECOGNITION_LEDGER_ENTRY_TYPES = [
  'opening',
  'allocation',
  'debit',
  'credit',
  'adjustment',
  'expiry',
  'redemption',
] as const;
export type RecognitionLedgerEntryType = (typeof RECOGNITION_LEDGER_ENTRY_TYPES)[number];

/** A `RewardRedemption`'s lifecycle (R-PRF-25). The points ledger/balance is debited only on the
 * transition into `fulfilled` — never at `pending` or `approved`. */
export const REWARD_REDEMPTION_STATUSES = ['pending', 'approved', 'fulfilled', 'cancelled'] as const;
export type RewardRedemptionStatus = (typeof REWARD_REDEMPTION_STATUSES)[number];

export const ONE_ON_ONE_STATUSES = ['scheduled', 'completed', 'cancelled', 'no_show'] as const;
export type OneOnOneStatus = (typeof ONE_ON_ONE_STATUSES)[number];

/** PRF-15: completing a meeting whose `recurrence` isn't `none` auto-creates the next occurrence. */
export const ONE_ON_ONE_RECURRENCES = ['none', 'weekly', 'fortnightly', 'monthly'] as const;
export type OneOnOneRecurrence = (typeof ONE_ON_ONE_RECURRENCES)[number];

export const ACTION_ITEM_STATUSES = ['open', 'done', 'cancelled'] as const;
export type ActionItemStatus = (typeof ACTION_ITEM_STATUSES)[number];

// ---------------------------------------------------------------------------
// Performance — Reviews
// ---------------------------------------------------------------------------

/** A `ReviewCycle`'s cadence — CLAUDE.md invariant 4 territory: what the cycle covers is a legal-
 * entity/fiscal-year question, never derived from the server's calendar. */
export const REVIEW_CYCLE_TYPES = [
  'annual',
  'half_yearly',
  'quarterly',
  'probation',
  'project',
] as const;
export type ReviewCycleType = (typeof REVIEW_CYCLE_TYPES)[number];

/**
 * A `ReviewCycle`'s lifecycle, in order. `released` (performance remediation §5G) sits between
 * calibration and close: `releaseResults` makes manager ratings and final scores visible to
 * employees, and `closeCycle` is only legal from it. Stored as text (no DB enum), so adding a value
 * needs no migration.
 */
export const REVIEW_CYCLE_STATUSES = [
  'draft',
  'active',
  'in_calibration',
  'released',
  'closed',
] as const;
export type ReviewCycleStatus = (typeof REVIEW_CYCLE_STATUSES)[number];

/**
 * A `PerformanceReview`'s stage, mirrored FROM the underlying `WorkflowInstance` once one exists —
 * never authored independently (see the plan's "the 4-stage UI tracker is NOT 4 workflow steps"
 * design). Kept as a separate column from `status` deliberately: a rejected review returns to
 * `manager_review`-adjacent stage while its `status` still shows it was once submitted, and
 * collapsing the two makes that state inexpressible.
 */
export const REVIEW_STAGES = [
  'goal_evaluation',
  'competency_assessment',
  'peer_feedback',
  'manager_review',
  'calibration',
  'closed',
] as const;
export type ReviewStage = (typeof REVIEW_STAGES)[number];

/**
 * A `PerformanceReview`'s status, in lifecycle order (performance remediation §6). `released` is
 * set when HR releases results (cycle- or per-review); only from there may the employee
 * `acknowledge` or appeal (`appealed`, back to `released` on rejection). `cancelled` is the
 * pre-release exit and stays last. Stored as text (no DB enum).
 */
export const REVIEW_STATUSES = [
  'not_started',
  'in_progress',
  'submitted',
  'manager_reviewed',
  'released',
  'acknowledged',
  'appealed',
  'closed',
  'cancelled',
] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

/** Core/Leadership/Functional — a `Competency`'s grouping for the admin catalogue and the rubric
 * drawer, no scoring weight of its own (that lives on `ReviewTemplateCompetency.weightPercent`). */
export const COMPETENCY_CATEGORIES = ['core', 'leadership', 'functional'] as const;
export type CompetencyCategory = (typeof COMPETENCY_CATEGORIES)[number];

/** A `ReviewEvidence` row's kind — a link, an uploaded document (via the existing `Attachment`
 * model), or a pointer back to a `Goal`. */
export const REVIEW_EVIDENCE_KINDS = ['link', 'document', 'goal'] as const;
export type ReviewEvidenceKind = (typeof REVIEW_EVIDENCE_KINDS)[number];

/** PRF-8 — custom review questions. A `ReviewTemplateQuestion`'s answer shape. Kept identical, by
 * value, to `@hrms/custom-fields`' `CUSTOM_FIELD_TYPES` (that package's own header explains why
 * this package does not take a dependency on it: `@hrms/shared` stays self-contained and RN-safe,
 * and every other domain enum in this file is likewise declared locally rather than imported from
 * a sibling package). Extending this list means extending `CUSTOM_FIELD_TYPES` and
 * `validateCustomFieldValue`'s switch to match. */
export const REVIEW_QUESTION_TYPES = ['rating', 'text', 'single_choice', 'multiple_choice'] as const;
export type ReviewQuestionType = (typeof REVIEW_QUESTION_TYPES)[number];

/** Which participant in a review answers a given `ReviewTemplateQuestion` — self, the direct
 * manager, a peer reviewer, an upward (subordinate-on-manager) reviewer, or (PRF-13) a skip-level
 * (grand-report-on-manager) reviewer. A question can be answered by more than one reviewer type
 * (`reviewerTypes` is an array on the question). `peer`/`upward`/`skip_level` questions are
 * answerable once a `FeedbackRequest` exists for that relationship — the peer-nomination flow
 * (PRF-13) is what creates one for `upward`/`skip_level`; ordinary self-service 360° requests
 * already cover `peer`. */
export const REVIEW_QUESTION_REVIEWER_TYPES = [
  'self',
  'manager',
  'peer',
  'upward',
  'skip_level',
] as const;
export type ReviewQuestionReviewerType = (typeof REVIEW_QUESTION_REVIEWER_TYPES)[number];

// ---------------------------------------------------------------------------
// Performance — Insights (Section 5)
// ---------------------------------------------------------------------------

/**
 * The cohort a rating-distribution/completion/talent-grid/skill-gap read is scoped to. Deliberately
 * NOT the general-purpose `SCOPES` (permissions/roles.ts) — that axis answers "who may call this",
 * this one answers "which pre-aggregated cohort to read", and it must match `ReviewCycleRollup`'s
 * `scopeType` column exactly since a closed cycle is served from that table verbatim. A manager's
 * own reporting-tree cohort is requested as `scope: 'team'` with no `scopeId` (resolved from the
 * caller, same as `reporting_tree`'s relative-scope convention); `legal_entity`/`department`/`org`
 * carry (or, for `org`, omit) a `scopeId` naming the specific entity. See
 * `docs/plans/performance-management/performance-insights-plan.md`'s Data model section.
 */
export const REVIEW_CYCLE_ROLLUP_SCOPES = ['team', 'department', 'legal_entity', 'org'] as const;
export type ReviewCycleRollupScope = (typeof REVIEW_CYCLE_ROLLUP_SCOPES)[number];

/** A `getTeamBreakdown`/`getAttentionList` row's derived status badge — computed on read from the
 * person's `overallScore`/goal progress/days overdue, never stored. */
export const INSIGHTS_PERSON_STATUSES = ['on_track', 'needs_attention', 'at_risk'] as const;
export type InsightsPersonStatus = (typeof INSIGHTS_PERSON_STATUSES)[number];

// ---------------------------------------------------------------------------
// Performance — Merit & Compensation Planning (PRF-9 / PRF-29)
// ---------------------------------------------------------------------------

/** A `MeritMatrix`'s lifecycle. Only `published` is read by `apps/worker`'s
 * merit-cycle-trigger.ts at cycle close — a `draft` matrix produces no recommendations, same
 * no-op posture as `ReviewCycle.highPerformerThreshold` being null. */
export const MERIT_MATRIX_STATUSES = ['draft', 'published'] as const;
export type MeritMatrixStatus = (typeof MERIT_MATRIX_STATUSES)[number];

/** A `MeritMatrixCell`'s optional second dimension — where the employee's current pay sits
 * against their grade's range. Optional because most orgs run a matrix on rating band alone
 * (`null`, applies regardless of position); this dimension is opt-in for orgs that also want to
 * pay differently within a band depending on how much headroom is left in the range. */
export const PAY_BAND_POSITIONS = ['below_range', 'within_range', 'above_range'] as const;
export type PayBandPosition = (typeof PAY_BAND_POSITIONS)[number];

/** A `MeritRecommendation`'s worksheet lifecycle. `pending` = system-generated at cycle close,
 * untouched; `submitted` = the reporting manager has submitted their worksheet; `approved` /
 * `rejected` = the cycle's single `merit_cycle_budget` WorkflowInstance settled (never a
 * per-recommendation approval — see that entity type's own registration in
 * apps/api/src/lib/workflow-entity-types.ts). */
export const MERIT_RECOMMENDATION_STATUSES = [
  'pending',
  'submitted',
  'approved',
  'rejected',
] as const;
export type MeritRecommendationStatus = (typeof MERIT_RECOMMENDATION_STATUSES)[number];

/** A `MeritCompensationProposal`'s own state, distinct from the recommendation's — the row is
 * only ever created already `approved` (see that model's own comment); `consumed` is Payroll's to
 * write once it turns this into a real `Compensation` row, `cancelled` covers a proposal later
 * withdrawn before Payroll picks it up. */
export const MERIT_PROPOSAL_STATES = ['approved', 'consumed', 'cancelled'] as const;
export type MeritProposalState = (typeof MERIT_PROPOSAL_STATES)[number];

/** The 9-box talent-grid axis bands — `getTalentGrid` buckets `PerformanceReview.overallScore`
 * (performance axis) and `PerformanceReview.potentialRating` (potential axis) into these three tiers
 * each, for the classic 3x3 grid. */
export const TALENT_GRID_BANDS = ['low', 'medium', 'high'] as const;
export type TalentGridBand = (typeof TALENT_GRID_BANDS)[number];

// ---------------------------------------------------------------------------
// Billing & Subscription
// ---------------------------------------------------------------------------

/** How often a Subscription is billed. The 10%/20% multi-month discount is a pricing-engine
 * calculation (`@hrms/shared`'s billing/pricing.ts), not a separate rate stored per frequency. */
export const BILLING_FREQUENCIES = ['monthly', 'six_month', 'yearly'] as const;
export type BillingFrequency = (typeof BILLING_FREQUENCIES)[number];

/**
 * `pending_verification` covers BOTH "checkout started, online payment not yet confirmed" and
 * "offline proof submitted, awaiting finance approval" — the two paths converge on the same
 * status because from the entitlement gate's point of view they mean the same thing: not yet
 * active. `trialing` is the 14-day free trial window (trial-start sets this explicitly — it is
 * NOT the column default). `past_due` is this subscription's dunning state (billing module phase
 * plan's "15+ day overdue" intercept reads it, not a separate `dunning_overdue` value).
 * `suspended` is past_due gone terminal: entitlement is pulled but the subscription record (and
 * its billing history) survives, distinct from `cancelled` which is a voluntary/final exit.
 */
export const SUBSCRIPTION_STATUSES = [
  'pending_verification',
  'trialing',
  'active',
  'past_due',
  'suspended',
  'cancelled',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/** `pro_forma` is generated instantly at checkout for the offline path (bank details + reference,
 * before any money has moved); `tax_invoice` is the statutory GST document issued once payment is
 * confirmed — online capture or offline verification approval — and is the only one ever emailed
 * as a legal document. */
export const INVOICE_TYPES = ['pro_forma', 'tax_invoice'] as const;
export type InvoiceType = (typeof INVOICE_TYPES)[number];

export const INVOICE_STATUSES = ['unpaid', 'pending_verification', 'paid', 'overdue', 'void'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const BILLING_PAYMENT_MODES = ['online', 'offline_bank'] as const;
export type BillingPaymentMode = (typeof BILLING_PAYMENT_MODES)[number];

export const OFFLINE_PAYMENT_VERIFICATION_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type OfflinePaymentVerificationStatus = (typeof OFFLINE_PAYMENT_VERIFICATION_STATUSES)[number];

/**
 * The fixed set of gateable feature modules — every plan's `features.modules.included`, every
 * Subscription's `deselectedModuleKeys`, and every `OrgModuleEntitlement.moduleKey` draw from this
 * list, and `requireModuleEntitlement()` (apps/api) rejects any other string. Matches the
 * permission-domain verification done before this module was built: `sso` and `custom_workflow`'s
 * "& APIs" half were checked against the real contract files and found unbuilt, so they are not
 * modules here — a toggle that gated nothing real would be a defect, not a feature.
 */
export const MODULE_KEYS = [
  'core_hr',
  'attendance',
  'statutory_payroll',
  'expense_reimbursement',
  'shift_rostering',
  'performance_okr',
  'ats_recruitment',
  'custom_workflow',
  'multi_entity',
  'leave',
  'projects',
  'onboarding',
  'asset_management',
  'recognition',
  'chat',
  'ai_copilot',
  // On unless an admin switches them off: never part of a plan (see lib/modules.ts).
  'helpdesk',
  'training',
  'engage',
  'offboarding',
  'travel',
  // Connected applications (ADR 0033): on unless switched off, like the five above.
  'app_integrations',
] as const;
export type ModuleKey = (typeof MODULE_KEYS)[number];

/** Where an org's entitlement to a module came from — `OrgModuleEntitlement.source`. */
export const MODULE_ENTITLEMENT_SOURCES = ['plan_default', 'addon', 'manual_grant'] as const;
export type ModuleEntitlementSource = (typeof MODULE_ENTITLEMENT_SOURCES)[number];

// ---- Asset Configuration & Vendors (Administration) ---------------------------

export const ASSET_DEPRECIATION_METHODS = ['none', 'straight_line', 'annual_amortization'] as const;
export type AssetDepreciationMethod = (typeof ASSET_DEPRECIATION_METHODS)[number];

export const ASSET_CUSTODY_SIGNOFFS = [
  'not_required',
  'optional',
  'required_physical',
  'required_sso',
] as const;
export type AssetCustodySignoff = (typeof ASSET_CUSTODY_SIGNOFFS)[number];

export const VENDOR_AMC_STATUSES = [
  'none',
  'active',
  'expiring_soon',
  'expired',
  'renewal_pending',
] as const;
export type VendorAmcStatus = (typeof VENDOR_AMC_STATUSES)[number];

// ---- Asset Batches (Administration) --------------------------------------------

/** Stored on AssetBatch.status. Archiving is the only lifecycle action — a batch is never deleted. */
export const ASSET_BATCH_STATUSES = ['active', 'archived'] as const;
export type AssetBatchStatus = (typeof ASSET_BATCH_STATUSES)[number];

/** NEVER stored — always derived at read time from assetCount/allocatedCount (see
 * asset-batch.contract.ts). 'archived' mirrors the batch's own stored status; the other three
 * describe where an active batch's assets stand. */
export const ASSET_BATCH_ALLOCATION_STATUSES = [
  'received',
  'partially_allocated',
  'fully_allocated',
  'archived',
] as const;
export type AssetBatchAllocationStatus = (typeof ASSET_BATCH_ALLOCATION_STATUSES)[number];

// ---- Helpdesk (ADR 0028) --------------------------------------------------------

/**
 * A helpdesk ticket's lifecycle. The legal moves are one table in `helpdesk.ts`. The resolution
 * SLA clock stops while `waiting_on_employee` and nowhere else; `resolved` is the desk's answer and
 * `closed` is the raiser accepting it (a rating) or the reopen window running out.
 */
export const HELPDESK_TICKET_STATUSES = [
  'open',
  'in_progress',
  'waiting_on_employee',
  'resolved',
  'closed',
] as const;
export type HelpdeskTicketStatus = (typeof HELPDESK_TICKET_STATUSES)[number];

export const HELPDESK_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;
export type HelpdeskPriority = (typeof HELPDESK_PRIORITIES)[number];

/** `internal` is never returned to the raiser — filtered by the API, not hidden by the client. */
export const HELPDESK_VISIBILITIES = ['public', 'internal'] as const;
export type HelpdeskVisibility = (typeof HELPDESK_VISIBILITIES)[number];

/** How a team's new tickets find an agent. */
export const HELPDESK_ASSIGNMENT_MODES = ['round_robin', 'manual'] as const;
export type HelpdeskAssignmentMode = (typeof HELPDESK_ASSIGNMENT_MODES)[number];

/**
 * What a ticket's SLA due instants were measured in: the location's working hours, or the clock.
 * Recorded per ticket so every screen can say which (ADR 0028 §6).
 */
export const HELPDESK_SLA_CLOCKS = ['business', 'calendar'] as const;
export type HelpdeskSlaClock = (typeof HELPDESK_SLA_CLOCKS)[number];

export const HELPDESK_SLA_KINDS = ['first_response', 'resolution'] as const;
export type HelpdeskSlaKind = (typeof HELPDESK_SLA_KINDS)[number];

/** One row of a ticket's activity history. */
export const HELPDESK_EVENT_KINDS = [
  'created',
  'status_changed',
  'assigned',
  'priority_changed',
  'category_changed',
  'commented',
  'attachment_added',
  'sla_breached',
  'reopened',
  'rated',
] as const;
export type HelpdeskEventKind = (typeof HELPDESK_EVENT_KINDS)[number];

// ---- Travel Desk (ADR 0030) ---------------------------------------------------

/**
 * A trip's stored lifecycle (plan §5.2). The legal moves are one table in `travel/trip-status.ts`;
 * an illegal move is a 409. `invited` is a colleague's trip nobody has accepted yet (D10); `settling`
 * is the expense window after the trip; `closed` means the money is settled. Upcoming / travelling
 * / returned are never stored — see `TRAVEL_TRIP_PHASES`.
 */
export const TRAVEL_TRIP_STATUSES = [
  'invited',
  'draft',
  'pending',
  'approved',
  'needs_action',
  'booked',
  'rejected',
  'withdrawn',
  'cancelled',
  'expired',
  'settling',
  'closed',
] as const;
export type TravelTripStatus = (typeof TRAVEL_TRIP_STATUSES)[number];

/**
 * Where a trip stands against the calendar, DERIVED from its dates in the traveller's
 * `location.timezone` (the entity's when the location has none, EC-50). Never stored.
 */
export const TRAVEL_TRIP_PHASES = ['upcoming', 'travelling', 'returned'] as const;
export type TravelTripPhase = (typeof TRAVEL_TRIP_PHASES)[number];

/** Domestic = within the traveller's legal-entity country (EC-6). v1 builds domestic only. */
export const TRAVEL_TRIP_TYPES = ['domestic', 'international'] as const;
export type TravelTripType = (typeof TRAVEL_TRIP_TYPES)[number];

export const TRAVEL_JOURNEY_TYPES = ['one_way', 'round_trip', 'multi_city'] as const;
export type TravelJourneyType = (typeof TRAVEL_JOURNEY_TYPES)[number];

/** Who books: the travel desk, or the traveller themself where their grade allows it (D1). */
export const TRAVEL_BOOKING_MODES = ['desk', 'self'] as const;
export type TravelBookingMode = (typeof TRAVEL_BOOKING_MODES)[number];

/** How one leg of a trip travels. */
export const TRAVEL_LEG_MODES = ['flight', 'train', 'bus', 'cab'] as const;
export type TravelLegMode = (typeof TRAVEL_LEG_MODES)[number];

/**
 * What a `TravelEntitlement` row entitles: a leg mode, a hotel, a daily allowance, a flat sum when
 * the traveller stays with friends or family (lodging in lieu), or own-vehicle mileage.
 */
export const TRAVEL_ENTITLEMENT_MODES = [
  'flight',
  'train',
  'bus',
  'cab',
  'hotel',
  'per_diem',
  'lodging_in_lieu',
  'mileage',
] as const;
export type TravelEntitlementMode = (typeof TRAVEL_ENTITLEMENT_MODES)[number];

/**
 * The class of travel an entitlement allows or a leg is booked in. Rail classes are Indian
 * Railways' codes (1A, 2A, 3A, 3E, EC, CC, SL, 2S). Which mode each class belongs to is
 * `TRAVEL_CLASS_LEG_MODE`.
 */
export const TRAVEL_CLASSES = [
  'economy',
  'premium_economy',
  'business',
  'first',
  'rail_1a',
  'rail_2a',
  'rail_3a',
  'rail_3e',
  'rail_ec',
  'rail_cc',
  'rail_sl',
  'rail_2s',
  'bus_ac_sleeper',
  'bus_ac_seater',
  'bus_non_ac',
  'cab_standard',
  'cab_premium',
] as const;
export type TravelClass = (typeof TRAVEL_CLASSES)[number];

/** The leg mode each class belongs to — a flight leg may only carry an air class, and so on. */
export const TRAVEL_CLASS_LEG_MODE: Readonly<Record<TravelClass, TravelLegMode>> = Object.freeze({
  economy: 'flight',
  premium_economy: 'flight',
  business: 'flight',
  first: 'flight',
  rail_1a: 'train',
  rail_2a: 'train',
  rail_3a: 'train',
  rail_3e: 'train',
  rail_ec: 'train',
  rail_cc: 'train',
  rail_sl: 'train',
  rail_2s: 'train',
  bus_ac_sleeper: 'bus',
  bus_ac_seater: 'bus',
  bus_non_ac: 'bus',
  cab_standard: 'cab',
  cab_premium: 'cab',
});

/**
 * A city's cost tier for hotel caps and per diem. India is seeded from the HRA X / Y / Z city
 * classification (D26); a city not listed for its country counts as `tier_3`.
 */
export const TRAVEL_CITY_TIERS = ['tier_1', 'tier_2', 'tier_3'] as const;
export type TravelCityTier = (typeof TRAVEL_CITY_TIERS)[number];

/** How a policy rule was broken: `soft` needs a reason, `hard` blocks submission outright. */
export const TRAVEL_POLICY_VIOLATION_SEVERITIES = ['soft', 'hard'] as const;
export type TravelPolicyViolationSeverity = (typeof TRAVEL_POLICY_VIOLATION_SEVERITIES)[number];

/** What one desk booking is for: a leg (by its mode) or a stay. */
export const TRAVEL_BOOKING_KINDS = ['flight', 'train', 'bus', 'cab', 'hotel'] as const;
export type TravelBookingKind = (typeof TRAVEL_BOOKING_KINDS)[number];

/**
 * One `TravelBooking` row's lifecycle (a flight, hotel, etc. the desk is working) — not
 * `TravelBookingStatus` above, which is the simpler `not_booked | booked | cancelled` tracker on
 * the request itself (R-P10). `on_hold` while a change to the trip is being decided (EC-20);
 * `replaced` when a change produced a new booking (`replacesBookingId` points back).
 */
export const TRAVEL_DESK_BOOKING_STATUSES = [
  'pending',
  'options_shared',
  'on_hold',
  'confirmed',
  'declined',
  'cancelled',
  'replaced',
] as const;
export type TravelDeskBookingStatus = (typeof TRAVEL_DESK_BOOKING_STATUSES)[number];

/** Where bookings come from. Only `manual` (the desk records it) in v1 (D1). */
export const TRAVEL_BOOKING_PROVIDERS = ['manual'] as const;
export type TravelBookingProvider = (typeof TRAVEL_BOOKING_PROVIDERS)[number];

/** One row of a booking's activity trail. */
export const TRAVEL_BOOKING_EVENT_KINDS = [
  'created',
  'assigned',
  'unassigned',
  'options_shared',
  'option_selected',
  'option_expired',
  'put_on_hold',
  'hold_released',
  'overspend_raised',
  'confirmed',
  'declined',
  'sla_breached',
  'cancelled',
  'charge_overridden',
  'refund_received',
  'replaced',
  'document_added',
  'commented',
] as const;
export type TravelBookingEventKind = (typeof TRAVEL_BOOKING_EVENT_KINDS)[number];

/** Who paid a booking: the company through the desk, or the traveller (self-booked, then claimed). */
export const TRAVEL_PAID_BY = ['company', 'employee'] as const;
export type TravelPaidBy = (typeof TRAVEL_PAID_BY)[number];

/** Who bears a cancellation charge (D27): by reason by default; a desk override needs a note. */
export const TRAVEL_CHARGE_BORNE_BY = ['company', 'employee'] as const;
export type TravelChargeBorneBy = (typeof TRAVEL_CHARGE_BORNE_BY)[number];

/**
 * A cancelled booking's refund. `none` when nothing is due; `pending` until it arrives (chased
 * weekly, EC-18); `forfeited` when it is given up on. A credit note is a `TravelCredit`, not this.
 */
export const TRAVEL_REFUND_STATUSES = ['none', 'pending', 'received', 'forfeited'] as const;
export type TravelRefundStatus = (typeof TRAVEL_REFUND_STATUSES)[number];

/** Where a refund went back to. */
export const TRAVEL_REFUND_DESTINATIONS = ['company', 'employee', 'credit_note'] as const;
export type TravelRefundDestination = (typeof TRAVEL_REFUND_DESTINATIONS)[number];

/** What a change to a booked trip does. `desk_overspend` is raised automatically (D11, EC-14). */
export const TRAVEL_AMENDMENT_KINDS = [
  'change',
  'add_leg',
  'remove_leg',
  'cancel',
  'cut_short',
  'extend',
  'desk_overspend',
] as const;
export type TravelAmendmentKind = (typeof TRAVEL_AMENDMENT_KINDS)[number];

/** An amendment that needs no approval is created `approved`. */
export const TRAVEL_AMENDMENT_STATUSES = ['pending', 'approved', 'rejected', 'withdrawn'] as const;
export type TravelAmendmentStatus = (typeof TRAVEL_AMENDMENT_STATUSES)[number];

/** Why a trip was cancelled — decides who bears a cancellation charge by default (D27). */
export const TRAVEL_CANCEL_REASONS = ['business', 'personal', 'exit'] as const;
export type TravelCancelReason = (typeof TRAVEL_CANCEL_REASONS)[number];

/**
 * A settlement round (D13): `pre_trip` for self-booked fares (D29), `main` after the trip,
 * `supplementary` for late receipts within the window.
 */
export const TRAVEL_SETTLEMENT_KINDS = ['pre_trip', 'main', 'supplementary'] as const;
export type TravelSettlementKind = (typeof TRAVEL_SETTLEMENT_KINDS)[number];

/**
 * A settlement round's lifecycle (TD-4.5). Submitting creates the round already `pending` (its
 * approval runs); `approved` once every step agreed, with the per-expense decisions recorded;
 * `settled` once finance ran the batch net-off against the trip's advance. `rejected` sends every
 * expense back; `withdrawn` is the traveller pulling a pending round. `open` is reserved (a round
 * being gathered on the server) and no v1 route creates it.
 */
export const TRAVEL_SETTLEMENT_STATUSES = [
  'open',
  'pending',
  'approved',
  'settled',
  'rejected',
  'withdrawn',
] as const;
export type TravelSettlementStatus = (typeof TRAVEL_SETTLEMENT_STATUSES)[number];

/** The per-expense decision inside one settlement approval (D8). */
export const TRAVEL_SETTLEMENT_LINE_DECISIONS = ['pending', 'approved', 'rejected'] as const;
export type TravelSettlementLineDecision = (typeof TRAVEL_SETTLEMENT_LINE_DECISIONS)[number];

/**
 * The claims the system drafts for a finished trip from the policy (TD-4.4): the daily allowance,
 * the flat sum for nights with friends or family, and own-vehicle distance.
 */
export const TRAVEL_ALLOWANCE_CLAIM_KINDS = ['per_diem', 'lodging_in_lieu', 'mileage'] as const;
export type TravelAllowanceClaimKind = (typeof TRAVEL_ALLOWANCE_CLAIM_KINDS)[number];

/**
 * What made a claim look like a company-paid booking (TD-4.3): the bill number equals the booking
 * reference / PNR, the amount equals the booking's, the date is the booking's travel or booked day.
 * A flag for the approver, never a block.
 */
export const TRAVEL_DUPLICATE_MATCH_FIELDS = ['booking_reference', 'amount', 'date'] as const;
export type TravelDuplicateMatchField = (typeof TRAVEL_DUPLICATE_MATCH_FIELDS)[number];

/**
 * One row of an advance's recovery ledger, `expense_advance_recoveries.kind` (D4 ladder, D22, D23).
 * `claim_net_off` is a claim absorbed by the advance (the DB's one-net-off-per-claim unique index
 * keys on this exact value), `repayment` money the employee handed back, `payroll_deduction`
 * written once the payroll run that took it is FINALIZED (never at scheduling), `fnf_deduction` on
 * F&F approval, `write_off` the company absorbing the rest (org owner approved), `reversal`
 * undoing an earlier row (a wrong payroll deduction refunded through an `arrears` input, EC-58).
 */
export const EXPENSE_ADVANCE_RECOVERY_KINDS = [
  'claim_net_off',
  'repayment',
  'payroll_deduction',
  'fnf_deduction',
  'write_off',
  'reversal',
] as const;
export type ExpenseAdvanceRecoveryKind = (typeof EXPENSE_ADVANCE_RECOVERY_KINDS)[number];

/**
 * What an expense category is for, `expense_categories.kind` (TD-4.1): `other` is every ordinary
 * category; travel files its policy-priced drafts (TD-4.4) under the category of the matching
 * kind. Distinct from `EXPENSE_CATEGORY_USAGE_TYPES`, which changes how the claim form computes the
 * amount.
 */
export const EXPENSE_CATEGORY_KINDS = ['other', 'per_diem', 'lodging_in_lieu', 'mileage'] as const;
export type ExpenseCategoryKind = (typeof EXPENSE_CATEGORY_KINDS)[number];

/**
 * `expense_advance_payroll_recoveries.manual_reason` (D22, EC-38, EC-58/60): why a schedule row
 * left the automatic path. The planner writes the first three when payroll cannot recover at all
 * (the advance is in a currency the traveller's current entity does not pay in, no active
 * employment, no gross pay to take a share of). The reconcile job writes the rest:
 * - on a `removed` row: `run_deleted`, `run_cancelled`, `not_in_run` (the balance is scheduled
 *   again on the next run, see `RECOVERY_RESCHEDULE_REASONS`) and `input_deleted` (someone took the
 *   input off the run by hand; not rescheduled);
 * - on a `manual` row, for finance to resolve: `over_recovered` (the run took more than was still
 *   owed), `arrears_not_paid` (an arrears refund's run went away before paying it),
 *   `refund_target_missing` / `refund_exceeds_deduction` (the refund names no finalized deduction,
 *   or more than it took), `advance_closed` (the advance was settled meanwhile) and `no_requester`
 *   (nobody to attribute a carried-forward deduction to).
 */
export const RECOVERY_MANUAL_REASONS = [
  'currency_mismatch',
  'no_active_employment',
  'no_gross_pay',
  'run_deleted',
  'run_cancelled',
  'not_in_run',
  'input_deleted',
  'over_recovered',
  'arrears_not_paid',
  'refund_target_missing',
  'refund_exceeds_deduction',
  'advance_closed',
  'no_requester',
] as const;
export type RecoveryManualReason = (typeof RECOVERY_MANUAL_REASONS)[number];

/**
 * The `manual_reason` values on a `removed` schedule row after which the reconcile job schedules
 * the remaining balance again on the next open run (the run went away under the deduction).
 */
export const RECOVERY_RESCHEDULE_REASONS = ['run_deleted', 'run_cancelled', 'not_in_run'] as const;
export type RecoveryRescheduleReason = (typeof RECOVERY_RESCHEDULE_REASONS)[number];

/**
 * One row of an advance's payroll schedule, `expense_advance_payroll_recoveries.status` (D22):
 * `scheduled` while its input sits on an unfinalized run; `recovered` once that run is finalized
 * (the ledger row is written then); `removed` when the run was deleted or the input taken off
 * before finalize (no ledger row, EC-58/60); `manual` when payroll cannot recover it (entity or
 * currency mismatch, EC-38) and finance must.
 */
export const PAYROLL_RECOVERY_SCHEDULE_STATUSES = [
  'scheduled',
  'recovered',
  'removed',
  'manual',
] as const;
export type PayrollRecoveryScheduleStatus = (typeof PAYROLL_RECOVERY_SCHEDULE_STATUSES)[number];

/** `deduction` takes money out of pay; `arrears_refund` pays a wrong deduction back (EC-58). */
export const PAYROLL_RECOVERY_SCHEDULE_KINDS = ['deduction', 'arrears_refund'] as const;
export type PayrollRecoveryScheduleKind = (typeof PAYROLL_RECOVERY_SCHEDULE_KINDS)[number];

/**
 * A proposed advance write-off, `expense_advance_write_offs.status` (TD-4.8): finance proposes, the
 * org owner decides. The DB allows one live (`proposed`) write-off per advance.
 */
export const ADVANCE_WRITE_OFF_STATUSES = ['proposed', 'approved', 'rejected', 'withdrawn'] as const;
export type AdvanceWriteOffStatus = (typeof ADVANCE_WRITE_OFF_STATUSES)[number];

/** How a desk team's new bookings find an agent — the helpdesk's two modes (D16). */
export const TRAVEL_DESK_ASSIGNMENT_MODES = ['round_robin', 'manual'] as const;
export type TravelDeskAssignmentMode = (typeof TRAVEL_DESK_ASSIGNMENT_MODES)[number];

/** A `TravelDocument` — the module's own files (D18), not the shared `Attachment` table. */
export const TRAVEL_DOCUMENT_KINDS = [
  'ticket',
  'hotel_voucher',
  'invoice',
  'boarding_pass',
  'itinerary',
  'cancellation_note',
  'refund_note',
  'credit_note',
  'other',
] as const;
export type TravelDocumentKind = (typeof TRAVEL_DOCUMENT_KINDS)[number];

export const TRAVEL_VENDOR_TYPES = [
  'airline',
  'rail',
  'bus_operator',
  'cab_operator',
  'hotel',
  'travel_agency',
  'other',
] as const;
export type TravelVendorType = (typeof TRAVEL_VENDOR_TYPES)[number];

// ---- Training -------------------------------------------------------------------

export const TRAINING_TASK_STATUSES = ['draft', 'active', 'archived'] as const;
export type TrainingTaskStatus = (typeof TRAINING_TASK_STATUSES)[number];

export const TRAINING_CONTENT_KINDS = [
  'video_url',
  // An MP4/WebM uploaded to the private bucket via `contentUploadTicket`; played back through a
  // short-lived presigned GET, exactly like a `document`. Progress rules are video_url's.
  'video_upload',
  'document',
  'reading',
  'checklist',
  'quiz',
] as const;
export type TrainingContentKind = (typeof TRAINING_CONTENT_KINDS)[number];

/** Languages a course can be authored in. The first is the default when nothing says otherwise.
 * Adding one is a one-line change here plus its two names below. Right-to-left scripts (Urdu) are
 * left out until the player and builder can lay them out. */
export const TRAINING_LANGUAGES = ['en', 'hi', 'mr'] as const;
export type TrainingLanguage = (typeof TRAINING_LANGUAGES)[number];
export const DEFAULT_TRAINING_LANGUAGE: TrainingLanguage = 'en';

/** Each language written in itself — what a learner's language switcher shows. */
export const TRAINING_LANGUAGE_LABELS: Record<TrainingLanguage, string> = {
  en: 'English',
  hi: 'हिन्दी',
  mr: 'मराठी',
};

/** Each language's English name, so HR can find one they cannot read. */
export const TRAINING_LANGUAGE_ENGLISH_NAMES: Record<TrainingLanguage, string> = {
  en: 'English',
  hi: 'Hindi',
  mr: 'Marathi',
};

/** What the HR builder shows for a language: "मराठी (Marathi)" — the native name with the English
 * one beside it. English is just "English". */
export function trainingLanguageHrLabel(language: TrainingLanguage): string {
  const native = TRAINING_LANGUAGE_LABELS[language];
  const english = TRAINING_LANGUAGE_ENGLISH_NAMES[language];
  return native === english ? native : `${native} (${english})`;
}

/** Stored on TrainingAssignment.status (state machine, Training_Module_Plan.md §4.1). `started` is
 * "a session is open but no heartbeat has credited time yet" — distinct from `in_progress` (T2) so
 * the UI can show "Started" rather than implying progress that hasn't happened.
 * `overdue`/`due_soon`/`completed_late` are never stored — see TRAINING_DISPLAY_STATUSES and
 * trainingDisplayStatus() in ./training/display-status.js. */
export const TRAINING_ASSIGNMENT_STATUSES = [
  'not_started',
  'started',
  'in_progress',
  'completed',
  'incomplete',
  'cancelled',
] as const;
export type TrainingAssignmentStatus = (typeof TRAINING_ASSIGNMENT_STATUSES)[number];

/** What a client actually renders: the stored status, with `completed` split into on-time/late and
 * an `overdue`/`due_soon` overlay computed against `dueAt` and the current time. */
export const TRAINING_DISPLAY_STATUSES = [
  ...TRAINING_ASSIGNMENT_STATUSES,
  'completed_late',
  'overdue',
  'due_soon',
] as const;
export type TrainingDisplayStatus = (typeof TRAINING_DISPLAY_STATUSES)[number];

/** The four states a LEARNER sees (training redesign §5) — the six stored statuses folded down.
 * `overdue` / `due_soon` stay an overlay on top (see `trainingLearnerView()`), never a status. */
export const TRAINING_LEARNER_STATUSES = [
  'not_started',
  'in_progress',
  'completed',
  'not_completed',
] as const;
export type TrainingLearnerStatus = (typeof TRAINING_LEARNER_STATUSES)[number];

/** Where a `video_url` lesson's link points when it is not a plain video file: played through the
 * provider's own embedded player. Uploaded videos are required on strict-mode courses. */
export const TRAINING_EMBED_PROVIDERS = ['youtube', 'vimeo'] as const;
export type TrainingEmbedProvider = (typeof TRAINING_EMBED_PROVIDERS)[number];

// ---- Certificate designs (training redesign PLAN §13) ----------------------------------------
/** `classic`: the built-in layout. `custom_background`: HR's own A4 artwork with fields placed on it. */
export const TRAINING_CERTIFICATE_LAYOUTS = ['classic', 'custom_background'] as const;
export type TrainingCertificateLayout = (typeof TRAINING_CERTIFICATE_LAYOUTS)[number];
export const TRAINING_CERTIFICATE_ORIENTATIONS = ['landscape', 'portrait'] as const;
export type TrainingCertificateOrientation = (typeof TRAINING_CERTIFICATE_ORIENTATIONS)[number];
/** Colour presets for the rendered PDF (server-side HTML, not UI tokens). */
export const TRAINING_CERTIFICATE_ACCENTS = ['navy', 'emerald', 'maroon', 'charcoal', 'gold'] as const;
export type TrainingCertificateAccent = (typeof TRAINING_CERTIFICATE_ACCENTS)[number];
/** `legal_entity`: the learner's company logo (Settings › Company). `custom`: one uploaded for this design. */
export const TRAINING_CERTIFICATE_LOGO_SOURCES = ['legal_entity', 'custom', 'none'] as const;
export type TrainingCertificateLogoSource = (typeof TRAINING_CERTIFICATE_LOGO_SOURCES)[number];
/** Validity of any certificate (course or external) at a moment: `expiring` = within 30 days. */
export const TRAINING_CERTIFICATE_VALIDITIES = ['valid', 'expiring', 'expired', 'revoked'] as const;
export type TrainingCertificateValidity = (typeof TRAINING_CERTIFICATE_VALIDITIES)[number];

/** `TrainingSession.cameraState` (Training_Module_Plan.md §3.3/§4.2). */
export const TRAINING_CAMERA_STATES = [
  'live',
  'denied',
  'unavailable',
  'waived',
  'not_required',
] as const;
export type TrainingCameraState = (typeof TRAINING_CAMERA_STATES)[number];

/** `TrainingSession.endReason` (§5.2). */
export const TRAINING_SESSION_END_REASONS = [
  'paused',
  'completed',
  'camera_lost',
  'idle',
  'tab_closed',
  'switched',
  'timeout',
  'released',
] as const;
export type TrainingSessionEndReason = (typeof TRAINING_SESSION_END_REASONS)[number];

/** A heartbeat the server rejects returns one of these instead of crediting time (§4.2). The client
 * pauses the player on any value. */
export const TRAINING_PAUSE_REASONS = ['camera_lost', 'segments_stale', 'not_visible'] as const;
export type TrainingPauseReason = (typeof TRAINING_PAUSE_REASONS)[number];

/** `TrainingAssignment.verificationStatus`, computed at completion (§5.4). */
export const TRAINING_VERIFICATION_STATUSES = [
  'pending',
  'verified',
  'gaps_detected',
  'not_recorded',
  'waived',
] as const;
export type TrainingVerificationStatus = (typeof TRAINING_VERIFICATION_STATUSES)[number];

/** `TrainingQuizQuestion.type` (§4.5, D14). */
export const TRAINING_QUIZ_QUESTION_TYPES = ['single_choice', 'multi_choice', 'true_false'] as const;
export type TrainingQuizQuestionType = (typeof TRAINING_QUIZ_QUESTION_TYPES)[number];

/** `POST /training/assignments/:id/report-issue` body `category` (§3.2/§5.3). */
export const TRAINING_ISSUE_CATEGORIES = [
  'camera_unavailable',
  'camera_denied',
  'content_broken',
  'other',
] as const;
export type TrainingIssueCategory = (typeof TRAINING_ISSUE_CATEGORIES)[number];

/**
 * Live (instructor-led) training sessions — a scheduled meeting, unlike the self-paced
 * `TrainingSession` working stint above. `zoho_meeting` creates the meeting through the org's
 * connected Zoho Meeting account and pulls the participant report back afterwards; `manual` is a
 * pasted link (Google Meet, Teams, anything) whose clicks are still tracked but whose attendance is
 * marked by hand, because there is no report to pull.
 */
export const TRAINING_LIVE_SESSION_PROVIDERS = ['zoho_meeting', 'manual'] as const;
export type TrainingLiveSessionProvider = (typeof TRAINING_LIVE_SESSION_PROVIDERS)[number];

/** Who a live session invites. `selected` = the people HR picked; `organisation` = every active
 *  employee within the scheduler's scope, expanded to one invitee row each when the session is
 *  created (or switched to it). Separate from `allowWalkIns`, which lets anyone signed in to the
 *  organisation join through the shared link. */
export const TRAINING_LIVE_SESSION_AUDIENCES = ['selected', 'organisation'] as const;
export type TrainingLiveSessionAudience = (typeof TRAINING_LIVE_SESSION_AUDIENCES)[number];

/** `live`/`ended` are never stored — they derive from `startsAt`/`endsAt` and the current time, the
 *  same way `overdue` derives for assignments. `completed` is set once attendance is final. */
export const TRAINING_LIVE_SESSION_STATUSES = ['scheduled', 'cancelled', 'completed'] as const;
export type TrainingLiveSessionStatus = (typeof TRAINING_LIVE_SESSION_STATUSES)[number];

/** `pending` until the session ends and attendance is synced or marked. `excused` is approved leave
 *  on the session's date, or an HR override with a note. */
export const TRAINING_LIVE_ATTENDANCE_STATUSES = [
  'pending',
  'present',
  'late',
  'left_early',
  'absent',
  'excused',
] as const;
export type TrainingLiveAttendanceStatus = (typeof TRAINING_LIVE_ATTENDANCE_STATUSES)[number];

/** Who decided an invitee's attendance status. `provider` = the meeting provider's participant
 *  report; `system` = derived without a report (approved leave → excused, no join → absent). */
export const TRAINING_LIVE_ATTENDANCE_SOURCES = ['provider', 'manual', 'system'] as const;
export type TrainingLiveAttendanceSource = (typeof TRAINING_LIVE_ATTENDANCE_SOURCES)[number];

export const TRAINING_LIVE_ATTENDANCE_SYNC_STATUSES = [
  'not_applicable',
  'pending',
  'synced',
  'failed',
] as const;
export type TrainingLiveAttendanceSyncStatus = (typeof TRAINING_LIVE_ATTENDANCE_SYNC_STATUSES)[number];

/** Zoho runs a separate accounts + API host per data centre; the org's account lives in exactly one.
 *  Indian customers are almost always `in`. `ca` is the one whose hosts are not `*.zoho.<region>` —
 *  Canada uses `*.zohocloud.ca`. */
export const ZOHO_REGIONS = ['com', 'in', 'eu', 'uk', 'com.au', 'jp', 'ca', 'sa'] as const;
export type ZohoRegion = (typeof ZOHO_REGIONS)[number];

/** The kinds of question a posting can ask on its apply form (recruitment R6). */
export const POSTING_QUESTION_KINDS = ['text', 'long_text', 'yes_no', 'choice', 'number'] as const;
export type PostingQuestionKind = (typeof POSTING_QUESTION_KINDS)[number];

/** What a failed knockout question does: mark the application for a recruiter, or reject it. */
export const KNOCKOUT_ACTIONS = ['flag', 'reject'] as const;
export type KnockoutAction = (typeof KNOCKOUT_ACTIONS)[number];

/** Background checks a hire can be put through (recruitment R7). */
export const CANDIDATE_CHECK_KINDS = ['identity', 'education', 'employment', 'criminal', 'address', 'reference'] as const;
export type CandidateCheckKind = (typeof CANDIDATE_CHECK_KINDS)[number];

/** `issue` means the check found something a person must look at; `waived` means it is not needed. */
export const CANDIDATE_CHECK_STATUSES = ['pending', 'in_progress', 'clear', 'issue', 'waived'] as const;
export type CandidateCheckStatus = (typeof CANDIDATE_CHECK_STATUSES)[number];
